// CreateCustomPage
// ================
//
// Wave 14bv Stage 5: Finger-Draw-Tour-Builder (klassisch, ein Stroke).
// Wave 14bv Stage 6c-d: Pin-Pin mit Guide-Stroke + Trail-Locking.
// Wave 14bv Stage 6e: Pin-Pin mit EXPLIZITER Trail-Auswahl per Tap.
//
// Zwei Modi, Toggle oben:
//
//   MODE "pins" (Default, Stage 6e):
//     1) Tap irgendwo auf die Karte → grüner Start-Pin.
//     2) Tap → roter Ziel-Pin.
//     3) Tap DIREKT auf eine sichtbare Trail-Linie → Trail wird in die
//        geordnete Liste aufgenommen und leuchtet gelb. Nochmal tippen =
//        entfernen. Drag/Pinch läuft normal (2-Finger) für Pan/Zoom.
//     4) Calculate → POST /api/navi/pin-route mit { start, end, trailIds }.
//        Backend fährt jeden gewählten Trail in Reihenfolge komplett durch,
//        Connector-Dijkstras zwischen Trails mit Strafe auf nicht-
//        ausgewählte Trails.
//
//   MODE "stroke" (Fallback / Power-User, Stage 5):
//     1 Finger = Freihand, 2 Finger = Pan/Zoom. Mehrere Strokes = ein Tour.
//     Backend: POST /api/navi/finger-route. Nutzt seit 6d dieselbe Stitch-
//     Logik mit Start/Ende aus erstem/letztem Sample.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import {
  buildLiguriaStyle,
  buildMapoutStyle,
  resolveTrailStyle,
} from '../lib/map/style';
import {
  addPmtilesArchive,
  registerPmtilesProtocol,
} from '../lib/map/pmtiles';
import { opfsStatus } from '../lib/opfsStore';
import { tilesManifest } from '../lib/api';
import {
  CORRIDOR_CENTER,
  DIFFICULTY_COLOR,
  DIFFICULTY_LABEL,
  DIFFICULTY_ORDER,
  REGIONS,
  trackToGeoJson,
} from '@adtu/shared';
import {
  postFingerRoute,
  postPinRoute,
  postSnapProbe,
  NaviRouteError,
  type FingerRouteResponse,
  type PinRouteResponse,
  type SnapProbeResult,
} from '../lib/naviApi';
import { useOnline } from '../lib/useOnline';
import { syncTrailCache, type CachedTrail } from '../lib/trails';
import { saveCustomTrail } from '../lib/customTrails';
import { mapIntentsBus } from '../lib/map/mapIntentsBus';
import { ephemeralRouteAsTrail } from '../lib/customTrailAsTrail';
import { pitstopTargetBus } from '../lib/pitstopTargetBus';
import { routeBus } from '../lib/routeBus';
import { raceRefBus } from '../lib/raceRefBus';
import { BatteryStationOverlay } from '../components/BatteryStationOverlay';
import { LocateControl } from '../lib/map/LocateControl';
import { mapBus } from '../lib/map/mapBus';
import { trailFilterBus } from '../lib/trailFilterBus';
import type { Difficulty } from '@adtu/shared';
import {
  ensurePoiIcon,
  ensurePoiSourceAndLayer,
  removePoiSymbol,
  setPoiData,
} from '../lib/map/poiSymbol';
import { usePoiDrinkingWater } from '../lib/usePois';
import { useBoolPreference, usePreference } from '../lib/usePreference';
import { useActiveRegion } from '../lib/useUiSettings';
import { isInsideAnyRegion, regionNamesList } from '../lib/regionCheck';
import { ensurePoisAvailableOffline, loadPoisWithRefresh } from '../lib/poi';
import { POIButton } from '../components/POIButton';
import { ScaleBar } from '../components/ScaleBar';
import { DifficultyLegend } from '../components/DifficultyLegend';
// Wave 14ca-fix64: Höhenprofil schon in der Planung anzeigen (User-Bug:
// "Wenn man eine Custom Tour kreiert, wäre es gut, wenn das Höhenprofil
// schon direkt bei der Planung angezeigt wird"). Wir bauen einen
// synthetischen GpxTrack aus den Route-Coordinates, fuettern den
// useTrailElevation-Hook mit einer synthetischen trailId — der Hook
// kuemmert sich um SRTM-Sampling + Caching wie bei normalen Trails.
import { ElevationProfile } from '../components/ElevationProfile';
import { useTrailElevation } from '../lib/useTrailElevation';
import {
  routeSegmentsToElevationSegments,
  uniqueTrailsFromSegments,
} from '../lib/routeSegmentMap';
import type { GpxTrack } from '@adtu/shared';

const STROKES_DONE_SOURCE = 'adt-strokes-done';
const STROKES_DONE_LAYER = 'adt-strokes-done-line';
const STROKES_LIVE_SOURCE = 'adt-strokes-live';
const STROKES_LIVE_LAYER = 'adt-strokes-live-line';
const ROUTE_SOURCE = 'adt-route';
const ROUTE_LAYER = 'adt-route-line';
const ROUTE_CASING_LAYER = 'adt-route-casing';
const ROUTE_HIGHLIGHT_LAYER = 'adt-route-highlight';
const ROUTE_HIGHLIGHT_GLOW_LAYER = 'adt-route-highlight-glow';

// Wave 14eq: Snap-Feedback. Eine Source fuer die "schwebenden Ringe" um
// die Pins (rot wenn kein Snap, gruen wenn ok), eine Source fuer die
// hervorgehobene Strasse zu der gesnappt wurde.
const SNAP_RINGS_SOURCE = 'adt-snap-rings';
const SNAP_RINGS_LAYER = 'adt-snap-rings-circle';
const SNAP_ROADS_SOURCE = 'adt-snap-roads';
const SNAP_ROADS_HALO_LAYER = 'adt-snap-roads-halo';
const SNAP_ROADS_LINE_LAYER = 'adt-snap-roads-line';

const TRAILS_SOURCE = 'adt-custom-trails';
const TRAILS_HALO_LAYER = 'adt-custom-trails-halo';
// Wave 14ca-fix64: zwei Layer fuer Stripe-Difficulties.
// extreme           → dashed schwarze Linie auf weisser Halo (b/w-Stripe)
// intermediate_plus → dashed blaue Linie auf roter Undercoat (blau/rot-Stripe)
const TRAILS_UNDERCOAT_LAYER = 'adt-custom-trails-undercoat';
const TRAILS_LINE_SOLID_LAYER = 'adt-custom-trails-line-solid';
const TRAILS_LINE_DASHED_LAYER = 'adt-custom-trails-line-dashed';
// Legacy-Layer-Name — wird nicht mehr neu hinzugefuegt, aber als Filter-
// Target im Click-Handler weiter referenziert. Layer wird in der Pipeline
// durch SOLID + DASHED ersetzt (siehe ensureLayer-Aufrufe weiter unten).
const TRAILS_LINE_LAYER = 'adt-custom-trails-line';
// Highlight-Layer liegt OBEN auf den normalen Trails und filtert auf die
// aktuell ausgewählten IDs. So kann der User sofort sehen was er schon
// eingesammelt hat.
const TRAILS_SELECTED_LAYER = 'adt-custom-trails-selected';

// Start/End-Marker pro Trail (grünes Dreieck = Start, rotes Viereck = Ende).
// Zeigt dem User die GPX-Richtung, damit er weiss wie herum er den Trail
// fahren würde wenn er ihn auswählt.
const TRAIL_ENDPOINTS_SOURCE = 'adt-custom-trail-endpoints';
const TRAIL_ENDPOINTS_START_LAYER = 'adt-custom-trail-endpoints-start';
const TRAIL_ENDPOINTS_END_LAYER = 'adt-custom-trail-endpoints-end';

const MIN_POINT_PX = 3;

// Tap-Detektor: kurzer Touch mit wenig Bewegung = Tap (für Pin-Placement
// oder Trail-Selection); alles andere = Draw (nur im stroke-Mode relevant).
const TAP_MAX_MS = 300;
const TAP_MAX_PX = 10;

// Trails tappen: wir fragen MapLibre nach Features in einem kleinen
// Rechteck um den Tap-Punkt. Größer = einfacher zu treffen auf dem Handy,
// aber zu gross wählt ständig den falschen Trail wenn sie nah beisammen
// liegen. 12px ist ein vernünftiger Kompromiss (entspricht ~1cm bei 96dpi).
const TRAIL_TAP_TOLERANCE_PX = 12;

type Stroke = Array<[number, number]>;
type Mode = 'pins' | 'stroke';
/**
 * Stage 6m / Wave 14bz — Planungs-Modus-Picker:
 *   - 'pin-to-pin': klassisch, Start + End beide per Tap
 *   - 'from-here':  Start = aktuelle GPS-Position, Ende per Tap
 *   - 'loop-here':  Loop, Start = End = aktuelle GPS-Position (Wave 14eh)
 *   - 'loop-pin':   Loop, Start = End = vom User getappte Position (Wave 14eh)
 *
 * Historie: 'loop' und 'multi-pin' wurden in Wave 14bz rausgenommen —
 * die Route-Qualität war damals schlecht (Loop: oft dieselben Trails
 * rueckwaerts; Multi-Pin: walk_failed-Warnings). Mit dem Tap-Trails-
 * Flow (Stage 6e) kommt Loop in Wave 14eh zurueck — User picked die
 * Trails explizit, also keine Router-Magie noetig.
 */
type PlanMode = 'pin-to-pin' | 'from-here' | 'loop-here' | 'loop-pin';
type RouteResult =
  | { kind: 'pin'; resp: PinRouteResponse }
  | { kind: 'stroke'; resp: FingerRouteResponse };

export function CreateCustomPage() {
  const navigate = useNavigate();
  const online = useOnline();
  // Wave 14cf: Region für Routing-Calls. Backend-Default ist 'liguria',
  // wenn der User im Onboarding 'elba' gewählt hat geht jeder finger-/
  // pin-route Request an navi-elba.sqlite.
  const [activeRegion] = useActiveRegion();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  // Stage 6h: Counter, der bei jedem `style.load` hochgezählt wird. Die
  // Daten-Sync-useEffects hängen an ihm als extra Dependency, damit sie
  // nach einem `setStyle` (PMTiles-Style-Upgrade) die frisch angelegten
  // leeren Sources erneut mit den aktuellen Daten befüllen.
  const [styleVersion, setStyleVersion] = useState(0);

  // Modus-State — pins = Default.
  const [mode, setMode] = useState<Mode>('pins');
  const modeRef = useRef<Mode>(mode);
  modeRef.current = mode;

  // Stage 6m — Planungs-Modus-Picker. null = noch nicht gewählt → Picker
  // wird als initiales Overlay angezeigt. Danach ist der Mode sticky für
  // diese Session (User kann über "Change plan" den Picker zurückholen).
  const [planMode, setPlanMode] = useState<PlanMode | null>(null);
  // Wave 14bz: Ref für Tap-Handler (sieht sonst Stale-Closure vom initial
  // Map-Mount-Effect).
  const planModeRef = useRef<PlanMode | null>(null);
  planModeRef.current = planMode;
  // Flag damit wir nur beim Setzen von from-here den GPS-Fix einmal
  // abholen, nicht bei jedem Re-Render oder Mode-Wechsel zurück zu
  // pin-to-pin.
  const planModeSetRef = useRef<PlanMode | null>(null);

  // Stage 6m — aktuelle GPS-Position für "jetzige Position"-Button. Wird
  // lazy per navigator.geolocation.getCurrentPosition abgeholt wenn der
  // User den Button drückt oder einen from-here/loop-Modus wählt.
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);

  // ----- Stroke-Mode-State -----
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const strokesRef = useRef<Stroke[]>([]);
  strokesRef.current = strokes;
  const currentStrokeRef = useRef<Stroke | null>(null);
  const lastPointPxRef = useRef<[number, number] | null>(null);

  // ----- Pins-Mode-State -----
  const [startPin, setStartPin] = useState<[number, number] | null>(null);
  const [endPin, setEndPin] = useState<[number, number] | null>(null);

  const startPinRef = useRef<[number, number] | null>(null);
  const endPinRef = useRef<[number, number] | null>(null);
  startPinRef.current = startPin;
  endPinRef.current = endPin;
  const startMarkerRef = useRef<maplibregl.Marker | null>(null);
  const endMarkerRef = useRef<maplibregl.Marker | null>(null);

  // Wave 14eq: Pin-Snap-Feedback. Pro Pin (start/end/via-id) merken wir
  // uns ob er sauber auf eine Strasse gesnappt hat (gruener Marker-Border
  // + Highlight der Strasse) oder im Niemandsland (roter Border). Probe
  // wird asynchron nach jedem setStartPin/setEndPin/Via-add gefeuert.
  //
  // 'pending' = Probe noch nicht zurueck, 'snapped' = OK, 'far' = kein
  // Way im Radius. Bei 'snapped' enthaelt snap.wayGeometry die Polyline
  // des Wegs zum Highlighten.
  type PinSnapState =
    | { status: 'pending' }
    | { status: 'snapped'; snap: NonNullable<SnapProbeResult['snap']> }
    | { status: 'far' };
  const [snapResults, setSnapResults] = useState<
    Record<string, PinSnapState>
  >({});

  // Shared Probe-Helper: feuert /api/navi/snap-position mit dem zoom-
  // adaptiven Radius (wie Calculate) und schreibt das Ergebnis in
  // snapResults[key]. Stale-Race-Schutz via key — wenn der User schnell
  // tippt, gewinnt der letzte Probe-Call. Wird via Ref aus den
  // Touch-Handlers im map-init-Effekt aufgerufen (sonst Stale-Closure
  // auf activeRegion).
  const probeSnapRef = useRef<
    ((key: string, ll: [number, number]) => void) | null
  >(null);
  const probeSnap = useCallback(
    async (key: string, ll: [number, number]) => {
      const map = mapRef.current;
      const zoom = map?.getZoom() ?? 14;
      const radius = Math.min(
        2000,
        Math.max(120, 120 * Math.pow(2, Math.max(0, 15 - zoom))),
      );
      setSnapResults((prev) => ({ ...prev, [key]: { status: 'pending' } }));
      try {
        const res = await postSnapProbe({
          lat: ll[1],
          lon: ll[0],
          region: activeRegion,
          radiusM: Math.round(radius),
        });
        setSnapResults((prev) => ({
          ...prev,
          [key]: res.snap
            ? { status: 'snapped', snap: res.snap }
            : { status: 'far' },
        }));
      } catch {
        // Network / Server-fehler → neutral lassen
        setSnapResults((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [activeRegion],
  );
  probeSnapRef.current = probeSnap;

  // Geordnete Waypoint-Liste (Stage 6m): mischt Trail-Selektionen und
  // Via-Pins in Tap-Reihenfolge. selectedTrailIds und viaPoints werden
  // davon abgeleitet, damit Down-Stream-Code (Map-Layer-Filter, UI-Badges,
  // etc.) ohne Änderung weiterläuft.
  type Waypoint =
    | { kind: 'trail'; trailId: string }
    | { kind: 'via'; lonLat: [number, number]; id: string };
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const waypointsRef = useRef<Waypoint[]>([]);
  waypointsRef.current = waypoints;

  // Backwards-Compat-Derivation. WICHTIG: `selectedTrailIds` ist der
  // ordered Subset für Trail-Layer-Filter und API-Legacy-Calls.
  const selectedTrailIds = useMemo(
    () =>
      waypoints
        .filter((w): w is Extract<Waypoint, { kind: 'trail' }> => w.kind === 'trail')
        .map((w) => w.trailId),
    [waypoints],
  );
  const selectedTrailIdsRef = useRef<string[]>([]);
  selectedTrailIdsRef.current = selectedTrailIds;
  const viaPoints = useMemo(
    () =>
      waypoints.filter(
        (w): w is Extract<Waypoint, { kind: 'via' }> => w.kind === 'via',
      ),
    [waypoints],
  );
  // Helfer für die Tap-Handler — operiert auf `setWaypoints` aber bietet
  // dieselbe Semantik wie der alte `setSelectedTrailIds` (Toggle / Filter /
  // Slice / Clear), damit der Refactor minimal bleibt.
  const setSelectedTrailIds = useCallback(
    (
      next:
        | string[]
        | ((prev: string[]) => string[]),
    ) => {
      setWaypoints((prev) => {
        const currentIds = prev
          .filter((w): w is Extract<Waypoint, { kind: 'trail' }> => w.kind === 'trail')
          .map((w) => w.trailId);
        const newIds = typeof next === 'function' ? next(currentIds) : next;
        // Wenn sich die Trail-Liste UND/oder ihre Reihenfolge geändert hat,
        // bauen wir die Waypoint-Liste neu auf: zuerst Trails in neuer
        // Reihenfolge, dann ALLE Vias am Ende. Das hält die Tap-Reihenfolge
        // GROB ein (Vias sind in jedem Fall NACH den Trails) — gut genug
        // für die existing call sites die nur Trails manipulieren. Wer
        // gemischte Reihenfolge braucht, ruft `setWaypoints` direkt.
        const vias = prev.filter(
          (w): w is Extract<Waypoint, { kind: 'via' }> => w.kind === 'via',
        );
        const trailWps: Waypoint[] = newIds.map((tid) => ({
          kind: 'trail',
          trailId: tid,
        }));
        return [...trailWps, ...vias];
      });
    },
    [],
  );

  // 2-Finger-Pan + Pinch-Zoom State.
  // Stage 6i: wir tracken zusätzlich zum Mittelpunkt den Finger-Abstand
  // zum Zeitpunkt des letzten Events, damit wir die Änderung als Zoom-
  // Delta auf die Map anwenden können (setZoom). Pinch + Pan läuft
  // parallel im gleichen 2-Finger-Gestik-Block.
  const twoFingerCenterPxRef = useRef<[number, number] | null>(null);
  const twoFingerDistPxRef = useRef<number | null>(null);

  // Tap-Detection-State.
  const touchStartPxRef = useRef<[number, number] | null>(null);
  const touchStartTimeRef = useRef<number>(0);
  const movedBeyondTapRef = useRef<boolean>(false);

  // Wave 14dy: Coarse-Pointer-Flag. Wird einmalig beim Map-Init gesetzt
  // (window.matchMedia('(pointer: coarse)')). Touch-Geraete = true →
  // dragPan AUS + 2-Finger-Gestik. Desktop = false → MapLibre's nativer
  // dragPan ist aktiv, unsere Mouse-Handler greifen NICHT mehr per
  // preventDefault ein wenn der User nur pannen will.
  const isCoarsePointerRef = useRef<boolean>(true);

  // Wave 14ed: Draw-Modifier-Key. Auf Desktop muss der User T halten um
  // im Stroke-Mode zu zeichnen (sonst pannt die Maus normal die Karte).
  // T wurde gewaehlt nach User-Feedback dass ALT/Option/Shift alle
  // entweder von MapLibre oder vom Browser gekapert wurden. T = "Tour"
  // und kollidiert mit gar nix.
  const tKeyDownRef = useRef<boolean>(false);

  // Wave 14dy: Einmalige Touch-Intro-Popup fuer Handy-User.
  // Auf coarse-Pointer + noch nie dismissed → einmaliger Hint, dass die
  // Karte mit 2 Fingern bewegt/gezoomt wird. Auf Desktop nie. Persistierter
  // Dismiss in localStorage, damit der Hinweis pro Geraet nur einmal kommt.
  const TOUCH_INTRO_SEEN_KEY = 'adt_create_custom_touch_intro_seen_v1';
  const [showTouchIntro, setShowTouchIntro] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia !== 'function') return false;
    if (!window.matchMedia('(pointer: coarse)').matches) return false;
    try {
      return window.localStorage.getItem(TOUCH_INTRO_SEEN_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const dismissTouchIntro = useCallback(() => {
    try {
      window.localStorage.setItem(TOUCH_INTRO_SEEN_KEY, '1');
    } catch {
      /* private-mode: harmless, just shows again next visit */
    }
    setShowTouchIntro(false);
  }, []);

  const [route, setRoute] = useState<RouteResult | null>(null);
  const [computing, setComputing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // ULTRA Pit-Stop: wenn der Finder ein Ziel "voraus" übergeben hat, setzen
  // wir hier Start+Ziel-Pin und berechnen die Route automatisch. pendingRef
  // signalisiert dem Auto-Calc-Effekt (nach handleCalculate definiert), dass
  // er feuern soll, sobald beide Pins im State stehen.
  const pitstopPendingRef = useRef(false);
  // ULTRA: kam diese Route aus dem Pit-Stop-Voraus-Flow (Navigation zu einem
  // POI)? Dann zeigen wir unten nur „RIDE" (fahren ohne zu speichern) statt
  // Save / Save & ride.
  const [fromPitstop, setFromPitstop] = useState(false);
  // Rennstrecke aus dem Pit-Stop-Target (routeBus ist beim Verlassen der Karte
  // schon geleert) — für die blasse Einblendung im Planer.
  const pitstopRaceRouteRef = useRef<Array<[number, number, number?]> | null>(null);
  useEffect(() => {
    const t = pitstopTargetBus.consume();
    if (!t) return;
    setFromPitstop(true);
    setPlanMode('pin-to-pin');
    setRoute(null);
    setStartPin(t.start);
    setEndPin(t.end);
    pitstopRaceRouteRef.current = t.raceRoute ?? null;
    // Rundfahrt: via = der POI, end = Wiedereinstieg auf die Rennstrecke. Als
    // Via-Waypoint setzen → Router fährt Start → POI → Strecke. Ohne via:
    // einfacher Weg zum POI (end = POI), keine Waypoints.
    if (t.via && t.via.length) {
      setWaypoints(
        t.via.map((v, i) => ({ kind: 'via' as const, lonLat: v, id: `pitstop-${i}` })),
      );
    } else {
      setWaypoints([]);
    }
    pitstopPendingRef.current = true;
  }, []);
  // Wave 14ca-fix21: Routing-Modus. 'mtb' = bisheriges Verhalten (Trails
  // freilich nutzbar). 'road' = nur Asphaltstrassen, alle Trails gesperrt
  // — für "schnell+sicher heim". Persistiert in localStorage damit der
  // User die Wahl beim nächsten Plan-Vorgang nicht erneut treffen muss.
  const [routeMode, setRouteMode] = useBoolPreference(
    'adt_route_mode_road',
    false,
  );
  // ULTRA: Rennrad-Routing. 'road' = kürzeste, 'flattest' = flachste (minimiert
  // Höhenmeter). MTB/Trails sind raus. Toggle persistiert in localStorage.
  const [flattest, setFlattest] = useBoolPreference('adt_route_flattest', false);
  const apiRouteMode: 'road' | 'flattest' = flattest ? 'flattest' : 'road';
  // Wave 14cv: Max-Difficulty-Cap. 'all' = kein Filter; sonst eine der
  // Difficulty-Strings. Persistiert in localStorage. User-Wunsch:
  // "wie schwer maximal die von der app ausgewählten trails sein dürfen,
  // sonst schickt sie anfänger auf schwarzen trails und es knallt".
  const [maxDifficultyRaw, setMaxDifficultyRaw] = usePreference(
    'adt_route_max_difficulty',
    'all',
  );
  const maxDifficulty = (
    maxDifficultyRaw === 'all' ||
    !DIFFICULTY_ORDER.includes(maxDifficultyRaw as Difficulty)
      ? null
      : (maxDifficultyRaw as Difficulty)
  );
  // Flash-Feedback wenn ein Trail-Tap ins Leere ging (kein Trail unter
  // dem Finger) — blendet nach 1.5s wieder aus.
  const [tapMissedAt, setTapMissedAt] = useState<number | null>(null);

  const [trails, setTrails] = useState<CachedTrail[]>([]);

  const activeTouchIdsRef = useRef<Set<number>>(new Set());

  // ---------------------------------------------------------------------
  // MapLibre-Init
  // ---------------------------------------------------------------------
  //
  // Stage 6h (Wave 14bv): gleicher Style-Look wie MapPage — Basemap +
  // Höhenlinien + (falls Hillshade-PMTiles im OPFS liegt) Hillshade.
  // Wir laden das PMTiles-Manifest async und bauen den richtigen Style
  // dynamisch. Ohne PMTiles → Fallback auf den reinen Liberty-Style
  // (wie bisher, online-only, kein Relief).
  useEffect(() => {
    if (!containerRef.current) return;

    // PMTiles-Protocol einmalig registrieren (idempotent in pmtiles.ts).
    registerPmtilesProtocol();

    // Wave 14dy: Coarse-Pointer-Detection. Touch-Geraete (Handy/Tablet)
    // bekommen weiter das 2-Finger-Pan-Verhalten — sonst wuerde der
    // einzelne Finger gleichzeitig versuchen Trails zu zeichnen UND die
    // Karte zu pannen, was sich nicht trennen laesst. Desktop-Geraete
    // haben keinen zweiten Finger, also: MapLibre uebernimmt dragPan
    // ueber die Maus, unsere mousedown/up-Handler bleiben fuer Pin-Taps
    // aktiv (wasTap-Detection — kleine Bewegung = Tap, grosse = Drag
    // schon von MapLibre konsumiert).
    const isCoarsePointer =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches;
    isCoarsePointerRef.current = isCoarsePointer;

    // Start mit einem Platzhalter-Style damit MapLibre sofort initialisiert.
    // Sobald das Manifest geladen ist, machen wir map.setStyle(richtiger).
    // Wave 14cf: Initial-Camera abhängig von der aktiven Region (Liguria
    // vs Elba). Sonst startet der Plan-Modus auf Liguria-Center, der User
    // muss erst manuell rüberpannen — schlechte UX.
    const initialCenter =
      REGIONS[activeRegion]?.center ?? CORRIDOR_CENTER;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildMapoutStyle(),
      center: initialCenter,
      zoom: 12,
      attributionControl: { compact: true },
      // Touch: dragPan AUS (eigene 2-Finger-Logik). Desktop: AN.
      dragPan: !isCoarsePointer,
      // Wave 14ec/ed: Alle MapLibre-Default-Gestiken die mit unseren
      // Modifiern kollidieren ausschalten.
      //   - dragRotate (ALT+Drag = Rotate) konkurrierte mit ALT-Draw.
      //   - boxZoom (Shift+Drag = Zoom-Rechteck) verwirrte User der
      //     glaubt er druecke ALT (der Shift-Pfeil sieht aehnlich aus).
      //   - pitchWithRotate / touchPitch hier nirgendwo gewollt.
      dragRotate: false,
      boxZoom: false,
      pitchWithRotate: false,
      touchPitch: false,
    });

    mapRef.current = map;

    // Wave 14bz: LocateControl wie auf der MapPage — grosser
    // orange-Kompass-Pfeil (Dreieck auf der Karte) sobald der User ihn
    // tappt. Zeigt Live-Position + zentriert die Karte auf den User.
    // `autoStart: false` damit die Permission-Abfrage nicht beim Seiten-
    // Öffnen kommt (sondern erst beim expliziten User-Tap auf den
    // Control, was auch iOS's User-Gesture-Requirement für die
    // Geolocation-Permission erfüllt).
    // Stage 6m: oneShot=true → Tap zentriert EINMAL und schaltet dann
    // nach 'paused', kein Auto-Follow. Der User kann frei pannen ohne dass
    // die Karte ihn zurückzieht. User-Wunsch im NAVI-Plan-Modus: "ich
    // bewege hin und her um die tour zu planen, sonst kommt sie immer
    // wieder in die mitte zrucük, das ist quatsch beim NAVI modus".
    const locate = new LocateControl({
      autoStart: false,
      lockZoom: 15,
      oneShot: true,
    });
    map.addControl(locate, 'top-right');

    // Manifest laden & richtigen Style setzen (best-effort, ohne blockieren).
    let styleSwapAborted = false;
    (async () => {
      try {
        const manifest = await tilesManifest();
        if (styleSwapAborted) return;
        const skipPmtiles = new URLSearchParams(location.search).has('nopmtiles');

        // Wave 14cf-fix: aus dem regions[]-Manifest die aktive Region picken
        // (Elba vs Liguria). Fallback auf Top-Level-Slots (alte Backend-Builds
        // ohne regions[] → zeigen automatisch auf Liguria).
        const regionEntry =
          manifest.regions?.find((r) => r.id === activeRegion) ??
          manifest.regions?.[0] ??
          null;
        const mapEntry = regionEntry?.map ?? manifest.map;
        const contoursEntry = regionEntry?.contours ?? manifest.contours;
        const hillshadeEntry = regionEntry?.hillshade ?? manifest.hillshade;

        if (skipPmtiles || !mapEntry) return;

        const mapUrl = `pmtiles://${location.origin}${mapEntry.url}`;
        const contourUrl = contoursEntry
          ? `pmtiles://${location.origin}${contoursEntry.url}`
          : undefined;
        const hillshadeAvailable =
          hillshadeEntry
            ? (await opfsStatus(`${location.origin}${hillshadeEntry.url}`))
                .exists
            : false;
        if (styleSwapAborted) return;
        const hillshadeUrl =
          hillshadeAvailable && hillshadeEntry
            ? `pmtiles://${location.origin}${hillshadeEntry.url}`
            : undefined;

        // Archives pre-registrieren, damit die pmtiles-Protocol-Handler die
        // File-Handles kennen wenn MapLibre gleich Tile-Requests feuert.
        addPmtilesArchive(mapUrl.slice('pmtiles://'.length));
        if (contourUrl) addPmtilesArchive(contourUrl.slice('pmtiles://'.length));
        if (hillshadeUrl) addPmtilesArchive(hillshadeUrl.slice('pmtiles://'.length));

        const richStyle = buildLiguriaStyle({
          mapPmtilesUrl: mapUrl,
          contourPmtilesUrl: contourUrl,
          hillshade: !!hillshadeUrl,
          hillshadePmtilesUrl: hillshadeUrl,
        });
        // diff:false wegen Source-/Layer-Struktur-Unterschied zum Starter-Style.
        map.setStyle(richStyle, { diff: false });
      } catch (err) {
        console.warn('[createCustom] style upgrade failed, sticking to mapout', err);
      }
    })();

    // `style.load` feuert sowohl beim initialen Style-Load als auch nach
    // jedem `setStyle` (z.B. nach dem PMTiles-Upgrade oben). Alle Custom-
    // Sources/Layers gehen bei setStyle verloren — wir registrieren sie hier
    // idempotent wieder (check `getSource`/`hasImage` vor jedem add).
    map.on('style.load', () => {
      // Icons für Start/End-Marker registrieren. Kopie aus MapView.tsx
      // (Wave 14bq) — grünes Dreieck für Trail-Start, rotes Viereck für Ende.
      const PT_SIZE = 28;
      const makeCanvas = (size: number) => {
        const c = document.createElement('canvas');
        c.width = size;
        c.height = size;
        return c;
      };
      if (!map.hasImage('adt-trail-start-triangle')) {
        const canvas = makeCanvas(PT_SIZE);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#22c55e';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.5;
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(PT_SIZE / 2, 4);
          ctx.lineTo(PT_SIZE - 4, PT_SIZE - 5);
          ctx.lineTo(4, PT_SIZE - 5);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          map.addImage(
            'adt-trail-start-triangle',
            ctx.getImageData(0, 0, PT_SIZE, PT_SIZE) as ImageData,
            { pixelRatio: 2 },
          );
        }
      }
      if (!map.hasImage('adt-trail-end-square')) {
        const canvas = makeCanvas(PT_SIZE);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ef4444';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.5;
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.rect(5, 5, PT_SIZE - 10, PT_SIZE - 10);
          ctx.fill();
          ctx.stroke();
          map.addImage(
            'adt-trail-end-square',
            ctx.getImageData(0, 0, PT_SIZE, PT_SIZE) as ImageData,
            { pixelRatio: 2 },
          );
        }
      }

      // Alle Sources/Layers idempotent: nur anlegen wenn noch nicht da.
      // Nach einem `setStyle` sind sie weg, beim re-fire von `style.load`
      // bauen wir sie neu; bei Double-Fire passiert nichts.
      const ensureSource = (id: string, spec: maplibregl.SourceSpecification) => {
        if (!map.getSource(id)) map.addSource(id, spec);
      };
      const ensureLayer = (spec: maplibregl.LayerSpecification) => {
        if (!map.getLayer(spec.id)) map.addLayer(spec);
      };

      // Trails-Layer (weisser Halo + difficulty-farbige Linie + Highlight).
      ensureSource(TRAILS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        promoteId: 'id',
      });
      ensureLayer({
        id: TRAILS_HALO_LAYER,
        type: 'line',
        source: TRAILS_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 3.5,
            12, 6.5,
            16, 10.5,
          ],
          'line-opacity': 0.9,
        },
      });
      // Wave 14ca-fix64: Undercoat fuer intermediate_plus.
      // Sitzt zwischen Halo (weiss) und blauer Dashed-Mainline; sichtbar
      // nur in den Gaps der Dashed-Linie → blau/rot-Stripe-Effekt.
      ensureLayer({
        id: TRAILS_UNDERCOAT_LAYER,
        type: 'line',
        source: TRAILS_SOURCE,
        filter: ['==', ['get', 'hasUndercoat'], true],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#d32f2f', // hard-rot, gleicher Hex wie in MapView
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 1.8,
            12, 3.5,
            16, 6,
          ],
        },
      });
      // Wave 14ca-fix64: Trail-Mainline split in SOLID + DASHED.
      // SOLID greift fuer alles ausser extreme + intermediate_plus.
      ensureLayer({
        id: TRAILS_LINE_SOLID_LAYER,
        type: 'line',
        source: TRAILS_SOURCE,
        filter: ['==', ['get', 'isDashed'], false],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'lineColor'],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 1.8,
            12, 3.5,
            16, 6,
          ],
        },
      });
      // DASHED: extreme (schwarz dashed auf weisser Halo) + intermediate_plus
      // (blau dashed auf roter Undercoat). MapLibre kann line-dasharray
      // nicht data-driven pro Feature, daher als separater Layer mit Filter.
      ensureLayer({
        id: TRAILS_LINE_DASHED_LAYER,
        type: 'line',
        source: TRAILS_SOURCE,
        filter: ['==', ['get', 'isDashed'], true],
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'lineColor'],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 1.8,
            12, 3.5,
            16, 6,
          ],
          'line-dasharray': [2, 1.5],
        },
      });
      ensureLayer({
        id: TRAILS_SELECTED_LAYER,
        type: 'line',
        source: TRAILS_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#fbbf24',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 4,
            12, 7,
            16, 11,
          ],
          'line-opacity': 0.95,
        },
        filter: ['in', ['get', 'id'], ['literal', []]],
      });

      ensureSource(TRAIL_ENDPOINTS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      ensureLayer({
        id: TRAIL_ENDPOINTS_START_LAYER,
        type: 'symbol',
        source: TRAIL_ENDPOINTS_SOURCE,
        minzoom: 10,
        filter: ['==', ['get', 'role'], 'start'],
        layout: {
          'icon-image': 'adt-trail-start-triangle',
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 0.55,
            14, 0.85,
            16, 1.1,
          ],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'center',
        },
      });
      ensureLayer({
        id: TRAIL_ENDPOINTS_END_LAYER,
        type: 'symbol',
        source: TRAIL_ENDPOINTS_SOURCE,
        minzoom: 10,
        filter: ['==', ['get', 'role'], 'end'],
        layout: {
          'icon-image': 'adt-trail-end-square',
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 0.55,
            14, 0.85,
            16, 1.1,
          ],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'center',
        },
      });

      ensureSource(STROKES_DONE_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      ensureLayer({
        id: STROKES_DONE_LAYER,
        type: 'line',
        source: STROKES_DONE_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#f97316',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 14,
            12, 22,
            16, 32,
          ],
          'line-opacity': 0.35,
        },
      });

      ensureSource(STROKES_LIVE_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      ensureLayer({
        id: STROKES_LIVE_LAYER,
        type: 'line',
        source: STROKES_LIVE_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#fb923c',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 16,
            12, 25,
            16, 36,
          ],
          'line-opacity': 0.5,
        },
      });

      // Wave 14eq: Snap-Feedback-Sources. ROADS unterhalb der Pins
      // damit sie nicht von Pin-Markern verdeckt werden. RINGS unten
      // damit sie als Halo um die Pin-DOM-Marker leuchten.
      ensureSource(SNAP_ROADS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      ensureLayer({
        id: SNAP_ROADS_HALO_LAYER,
        type: 'line',
        source: SNAP_ROADS_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 4,
            14, 8,
            18, 14,
          ],
          'line-opacity': 0.6,
        },
      });
      ensureLayer({
        id: SNAP_ROADS_LINE_LAYER,
        type: 'line',
        source: SNAP_ROADS_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ef4444',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 2,
            14, 4,
            18, 7,
          ],
          'line-opacity': 0.85,
        },
      });
      ensureSource(SNAP_RINGS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      ensureLayer({
        id: SNAP_RINGS_LAYER,
        type: 'circle',
        source: SNAP_RINGS_SOURCE,
        paint: {
          // Farbe pro Feature: gruen (snapped), rot (zu weit weg),
          // gelb (probing/unbekannt). Lesbar dank weissem Casing.
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-color': [
            'coalesce',
            ['get', 'color'],
            '#fbbf24',
          ],
          'circle-stroke-width': 3,
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 9,
            14, 13,
            18, 18,
          ],
          'circle-opacity': 1,
        },
      });

      ensureSource(ROUTE_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      // Stage 6m-fix4: Casing breit (~30% dicker als vorher, +50% bei
      // höchstem Zoom) damit die Route gegen die Difficulty-Trails
      // (line-width 1.8/3.5/6 bei z=8/12/16) klar auffällt. Casing ist
      // dunkel-trans, der Hauptstrich liest seine Farbe aus
      // feature.properties.color (gesetzt vom Routes-Effect je nach
      // Segment-Typ).
      ensureLayer({
        id: ROUTE_CASING_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#0c4a6e',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 5,
            12, 9,
            16, 13,
          ],
          'line-opacity': 0.7,
        },
      });
      ensureLayer({
        id: ROUTE_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        // Bridge-Features werden im separaten gestrichelten Layer drüber
        // gerendert — hier rausfiltern damit's nicht doppelt gemalt wird.
        filter: ['!=', ['get', 'kind'], 'bridge'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          // Pro-Feature-Farbe (Difficulty-Color für Trails, Sky für
          // Connector). Default wenn nicht gesetzt: Sky.
          'line-color': [
            'coalesce',
            ['get', 'color'],
            '#38bdf8',
          ],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 2.6,
            12, 5,
            16, 8,
          ],
          'line-opacity': 1,
        },
      });
      // Bridge-Layer: gestrichelt rot über die Lücken (no_connector).
      ensureLayer({
        id: 'adt-route-bridge',
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        filter: ['==', ['get', 'kind'], 'bridge'],
        paint: {
          'line-color': '#ef4444',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 2.6,
            12, 5,
            16, 8,
          ],
          'line-dasharray': [1, 1.5],
          'line-opacity': 0.95,
        },
      });

      // Stage 6m-fix5: Highlight-Layer. Wenn der User unten auf einen
      // Trail-Badge tippt, schalten wir den Filter auf den entsprechenden
      // trailId und der zugehörige Route-Abschnitt blitzt in Weiß auf —
      // mit weichem Glow drunter (line-blur). Default-Filter zielt auf
      // einen Sentinel-Wert, der nie matcht (null), damit der Layer
      // unsichtbar ist solange nichts ausgewählt ist.
      ensureLayer({
        id: ROUTE_HIGHLIGHT_GLOW_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        filter: ['==', ['get', 'trailId'], '__none__'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 8,
            12, 14,
            16, 20,
          ],
          'line-blur': 6,
          'line-opacity': 0.55,
        },
      });
      ensureLayer({
        id: ROUTE_HIGHLIGHT_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        filter: ['==', ['get', 'trailId'], '__none__'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 4.5,
            12, 8,
            16, 12,
          ],
          'line-opacity': 1,
        },
      });

      setMapReady(true);
      setStyleVersion((v) => v + 1);
    });

    return () => {
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
      if (endMarkerRef.current) {
        endMarkerRef.current.remove();
        endMarkerRef.current = null;
      }
      styleSwapAborted = true;
      mapRef.current = null;
      setMapReady(false);
      // Wave 14ca-fix27: mapBus-Restore passiert im mapReady-useEffect-
      // Cleanup (siehe unten) der den vorigen Bus-Wert merkt und
      // wiederherstellt. Hier nicht erneut auf null setzen — das wäre
      // ein zweiter Wipe der die Wiederherstellung kaputt macht.
      map.remove();
    };
    // Wave 14cf: activeRegion in den Deps damit ein Region-Wechsel
    // (z.B. User wechselt im Profile von Liguria zu Elba und kehrt
    // zurück zur Plan-Page) den Map-Rebuild triggert mit korrekten
    // PMTiles + Initial-Camera. Ohne diese Dep zeigt die Plan-Page
    // weiter Liguria-Tiles obwohl Elba aktiv ist.
  }, [activeRegion]);

  // Stage 6m: ShareableBottomBar (ScaleBar/DifficultyLegend/POIButton) braucht
  // den Map-Handle via mapBus. Wir publishen sobald `mapReady` true ist —
  // VOR mapReady ist die Map zwar instanziiert aber Style noch nicht geladen,
  // ScaleBar würde 0 km anzeigen.
  //
  // Wave 14ca-fix27: vorigen mapBus-Wert merken und beim Unmount wieder
  // herstellen statt auf null zu setzen. MapPage's MapView wird im Tab-
  // Wechsel NICHT neu gemounted (NAVI ist nur ein Overlay über MapPage),
  // deshalb hat ein hartes mapBus.set(null) zur Folge dass ScaleBar/Zoom-
  // Indicator/etc. nach Rückkehr zur MAP keine Map mehr sehen.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const previous = mapBus.get();
    mapBus.set(map);
    return () => {
      // Nur revoken wenn der Bus noch UNSERE Map zeigt; falls währenddessen
      // jemand anderes set() gemacht hat, lassen wir es in Ruhe.
      if (mapBus.get() === map) mapBus.set(previous);
    };
  }, [mapReady]);

  // ULTRA: aktive Rennstrecke (routeBus) als blasse Linie im Planer zeigen,
  // damit man beim Pit-Stop-Routen sieht, wo man wieder einsteigt.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const SRC = 'adt-race-route';
    const LYR = 'adt-race-route-line';
    const apply = () => {
      // Referenz: persistenter raceRefBus (überlebt Navigation), Fallback auf
      // die mit dem Pit-Stop übergebene Strecke bzw. routeBus.
      const route = raceRefBus.get() ?? pitstopRaceRouteRef.current ?? routeBus.get();
      const coords = route ? route.map(([lat, lon]) => [lon, lat]) : [];
      const data: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      };
      try {
        const src = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
        if (!src) {
          if (!coords.length) return;
          map.addSource(SRC, { type: 'geojson', data });
          map.addLayer({
            id: LYR,
            type: 'line',
            source: SRC,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': '#f59e0b',
              'line-width': 4,
              'line-opacity': 0.4,
              'line-dasharray': [1.5, 1.5],
            },
          });
        } else {
          src.setData(data);
        }
      } catch {
        /* Style noch nicht bereit — styledata holt's nach */
      }
    };
    const onStyle = () => apply();
    map.on('styledata', onStyle);
    apply();
    return () => {
      try {
        map.off('styledata', onStyle);
        if (map.getLayer(LYR)) map.removeLayer(LYR);
        if (map.getSource(SRC)) map.removeSource(SRC);
      } catch {
        /* Map zerstört */
      }
    };
  }, [mapReady]);

  // Stage 6m: Difficulty-Filter auf die Trail-Layer anwenden. trailFilterBus
  // ist die zentrale Single-Source-of-Truth (DifficultyLegend in der Bottom-
  // Bar schreibt rein, MapPage liest, jetzt auch CreateCustomPage). Wenn
  // der Filter leer ist (size 0), keine Filterung.
  const [diffFilter, setDiffFilter] = useState<ReadonlySet<Difficulty>>(
    () => new Set(trailFilterBus.get()?.difficulties ?? []),
  );
  // Wave 14eh-fix: hideAll-Flag mit-lesen (wie MapPage). Der "None"-Preset
  // in der DifficultyLegend setzt hideAll=true bei LEEREM difficulties-Set
  // (semantischer Konflikt: leeres Set = "alle zeigen", deshalb das separate
  // Flag). CreateCustomPage hat das bisher ignoriert → der None-Preset
  // versteckte keine Trails (User-Bug "Filter wirkt nicht").
  const [hideAllTrails, setHideAllTrails] = useState<boolean>(
    () => trailFilterBus.get()?.hideAll === true,
  );
  useEffect(() => {
    const sync = () => {
      const f = trailFilterBus.get();
      setDiffFilter(new Set(f?.difficulties ?? []));
      setHideAllTrails(f?.hideAll === true);
    };
    sync();
    return trailFilterBus.subscribe(sync);
  }, []);

  // ---------------------------------------------------------------------
  // Marker sync (pins-Mode)
  // ---------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (mode !== 'pins') {
      if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
      }
      if (endMarkerRef.current) {
        endMarkerRef.current.remove();
        endMarkerRef.current = null;
      }
      return;
    }

    if (startPin) {
      if (!startMarkerRef.current) {
        startMarkerRef.current = new maplibregl.Marker({
          element: makePinElement('start'),
          anchor: 'bottom',
        })
          .setLngLat(startPin)
          .addTo(map);
      } else {
        startMarkerRef.current.setLngLat(startPin);
      }
    } else if (startMarkerRef.current) {
      startMarkerRef.current.remove();
      startMarkerRef.current = null;
    }

    if (endPin) {
      if (!endMarkerRef.current) {
        endMarkerRef.current = new maplibregl.Marker({
          element: makePinElement('end'),
          anchor: 'bottom',
        })
          .setLngLat(endPin)
          .addTo(map);
      } else {
        endMarkerRef.current.setLngLat(endPin);
      }
    } else if (endMarkerRef.current) {
      endMarkerRef.current.remove();
      endMarkerRef.current = null;
    }
  }, [startPin, endPin, mode, mapReady]);

  // Stage 6m: Via-Marker. Wir halten eine Map id → Marker, um nicht bei
  // jedem Render alle Marker zu zerstören und neu zu erstellen (würde
  // beim ResizeObserver-Trigger Flickern verursachen). Beim Wechsel auf
  // stroke-Mode oder beim Clear werden alle Marker entfernt.
  const viaMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const markers = viaMarkersRef.current;
    // stroke-Mode → alle Vias verstecken (sie werden beim Mode-Switch
    // nicht gelöscht, weil der User wieder zurück auf pins gehen kann).
    if (mode !== 'pins') {
      for (const m of markers.values()) m.remove();
      markers.clear();
      return;
    }

    // Wave 14eh: ueber ALLE waypoints iterieren (nicht nur viaPoints),
    // damit jeder Via seine GLOBALE Position (1/2/3 …) kennt — gleich
    // der Nummer in der Chip-Row. So matched der Kartenmarker visuell
    // exakt das Chip beim Reordering.
    const viasWithIdx: Array<{
      id: string;
      lonLat: [number, number];
      label: number;
    }> = [];
    waypoints.forEach((wp, i) => {
      if (wp.kind === 'via') {
        viasWithIdx.push({ id: wp.id, lonLat: wp.lonLat, label: i + 1 });
      }
    });

    const liveIds = new Set(viasWithIdx.map((v) => v.id));
    // Alte entfernen die nicht mehr in der Liste sind.
    for (const [id, m] of markers.entries()) {
      if (!liveIds.has(id)) {
        m.remove();
        markers.delete(id);
      }
    }
    // Neue hinzufügen / Position + Label aktualisieren.
    for (const v of viasWithIdx) {
      const existing = markers.get(v.id);
      if (existing) {
        existing.setLngLat(v.lonLat);
        // Label live aktualisieren wenn sich die Reihenfolge geaendert
        // hat — keine Marker-Recreation noetig, einfach textContent
        // updaten.
        const el = existing.getElement();
        const wanted = String(v.label);
        if (el.dataset.viaLabel !== wanted) {
          el.textContent = wanted;
          el.dataset.viaLabel = wanted;
        }
      } else {
        // anchor:'center' weil das makePinElement('via') ein einzelner
        // Kreis ist (kein Tropfen mit Tail) — der geographic point
        // sitzt mittig im Element. Vermeidet die 'bottom'-Translate-Drift
        // bei Zoom (Stage 6m-fix).
        const marker = new maplibregl.Marker({
          element: makePinElement('via', v.label),
          anchor: 'center',
        })
          .setLngLat(v.lonLat)
          .addTo(map);
        markers.set(v.id, marker);
      }
    }
  }, [waypoints, mode, mapReady]);

  // Wave 14eq: Snap-Feedback-Layer-Sync. Aus snapResults + den jeweiligen
  // Pin-Positionen bauen wir zwei GeoJSON-FeatureCollections:
  //   - SNAP_RINGS_SOURCE: ein Punkt pro Pin, color-Property nach Status
  //     (#22c55e gruen wenn snapped, #ef4444 rot wenn far, gelb gelb wenn
  //     probing). Zeichnet ein farbiges Ring um den Pin.
  //   - SNAP_ROADS_SOURCE: pro snapped Pin ein LineString der gesnappten
  //     Strasse (aus snap.wayGeometry.segments), damit der User sieht
  //     AN WELCHE Strasse der Pin haftet.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || mode !== 'pins') return;
    const ringsSrc = map.getSource(SNAP_RINGS_SOURCE) as
      | maplibregl.GeoJSONSource
      | undefined;
    const roadsSrc = map.getSource(SNAP_ROADS_SOURCE) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (!ringsSrc || !roadsSrc) return;

    type Feat = GeoJSON.Feature<GeoJSON.Geometry, { color: string }>;
    const ringFeatures: Feat[] = [];
    const roadFeatures: Feat[] = [];

    const pushPin = (key: string, lonLat: [number, number]) => {
      const state = snapResults[key];
      if (!state) return; // noch nichts gepingt
      let color = '#fbbf24'; // pending = gelb
      if (state.status === 'snapped') color = '#22c55e';
      else if (state.status === 'far') color = '#ef4444';
      ringFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: lonLat },
        properties: { color },
      });
      if (state.status === 'snapped' && state.snap.wayGeometry?.segments) {
        // wayGeometry.segments ist [[ [lat, lon], [lat, lon] ], ...]
        // → wir bauen MultiLineString aus diesen Segmenten in [lon, lat].
        const lineCoords = state.snap.wayGeometry.segments.map((seg) => [
          [seg[0][1], seg[0][0]] as [number, number],
          [seg[1][1], seg[1][0]] as [number, number],
        ]);
        roadFeatures.push({
          type: 'Feature',
          geometry: { type: 'MultiLineString', coordinates: lineCoords },
          properties: { color: '#ef4444' },
        });
      }
    };

    if (startPin) pushPin('start', startPin);
    if (endPin) pushPin('end', endPin);
    for (const wp of waypoints) {
      if (wp.kind === 'via') pushPin(`via-${wp.id}`, wp.lonLat);
    }

    ringsSrc.setData({ type: 'FeatureCollection', features: ringFeatures });
    roadsSrc.setData({ type: 'FeatureCollection', features: roadFeatures });
  }, [snapResults, startPin, endPin, waypoints, mode, mapReady, styleVersion]);

  // Stage 6m: POI-Symbole (Trinkwasser) auch im Plan-Modus rendern. User-
  // Wunsch: "im planen / navi modus sollte ich auch die POIs auswählen
  // können weil ich eine route zB an wasserstellen vorbei plane". Dieselbe
  // Preference wie auf MapPage (`usePoiDrinkingWater`), dieselben Helpers
  // (lib/map/poiSymbol.ts). Wenn der User in MapPage POI an hatte, sieht
  // er sie hier auch sofort — und kann sie hier zusätzlich togglen via
  // dem POIButton im Header (siehe unten).
  const [drinkingWaterOn] = usePoiDrinkingWater();
  // Stage 6m: POI-Cache aufwärmen falls der User direkt auf NAVI lan-
  // det ohne MapPage vorher zu öffnen. ensurePoisAvailableOffline ist
  // idempotent (cached → no-op, leerer Cache → Backend-Fetch + Speichern
  // in IDB). Ohne diesen Vorlauf hätte loadPoisWithRefresh in der unteren
  // Effect-Branch nichts zu lesen und die User würde POIs aktivieren ohne
  // dass je ein Marker auftaucht.
  useEffect(() => {
    void ensurePoisAvailableOffline();
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (!drinkingWaterOn) {
      removePoiSymbol(map);
      return;
    }

    // Wave 14ca-fix10: gleiches applyOnce-Pattern wie in MapPage. Robuste
    // Voraussetzungs-Checks (style-loaded + features-loaded), drei Trigger
    // (features-loaded, mount-time, styledata-fired) rufen alle dieselbe
    // Funktion. Vorher hat ein styledata-Event vor features-load eine
    // leere Source registriert und es kam nie ein Re-Apply mit den echten
    // Daten — User-Bug "POI aktiv aber keine Marker sichtbar im NAVI".
    let cancelled = false;
    let featuresLoaded = false;
    let features: GeoJSON.Feature<GeoJSON.Point>[] = [];

    // Wave 14ca-fix20: Style-Loaded-Check ersetzt — `isStyleLoaded()` war in
    // CreateCustomPage's setStyle-Sequenz unzuverlässig (returnt permanent
    // false trotz fertigem style.load). Stattdessen versuchen wir applyOnce
    // immer, fangen den potentiellen "Style is not done loading"-Throw mit
    // einem try-catch ab und retrien dann beim nächsten Trigger. Plus ein
    // explizites map.once('idle') als finaler Trigger — feuert wenn die
    // Map komplett settled ist (Style + Sources + Tiles).
    let applyCount = 0;
    const applyOnce = (trigger: string) => {
      if (cancelled) {
        console.info(`[navi-poi] skip ${trigger}: cancelled`);
        return;
      }
      if (!featuresLoaded) {
        console.info(`[navi-poi] skip ${trigger}: features not loaded yet`);
        return;
      }
      try {
        ensurePoiIcon(map);
        ensurePoiSourceAndLayer(map);
        setPoiData(map, features);
        applyCount++;
        console.info(
          `[navi-poi] applied ${trigger} (#${applyCount}, ${features.length} features)`,
        );
      } catch (err) {
        // Beim ersten Versuch wirft MapLibre oft "Style is not done loading".
        // Wir loggen aber emitten keinen UI-Fehler — der nächste Trigger
        // (styledata / idle) fängt's ab sobald der Style ready ist.
        console.info(
          `[navi-poi] skip ${trigger}: not ready yet (${err instanceof Error ? err.message : String(err)})`,
        );
      }
    };

    void (async () => {
      try {
        const { pois, fromCache, error } = await loadPoisWithRefresh('drinking_water');
        if (cancelled) return;
        console.info('[navi] POI drinking_water:', {
          count: pois.length,
          fromCache,
          error,
        });
        features = pois.map(
          (p): GeoJSON.Feature<GeoJSON.Point> => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
            properties: { id: p.id, name: p.name ?? '' },
          }),
        );
        featuresLoaded = true;
        applyOnce('features-loaded');
      } catch (err) {
        console.warn('[navi] POI load failed', err);
      }
    })();

    // Bei Style-Swap (PMTiles-Upgrade nach Manifest-Load) müssen Source +
    // Layer + Image neu attached werden.
    const onStyleData = () => applyOnce('styledata');
    map.on('styledata', onStyleData);
    // Wave 14ca-fix20: Zusätzlicher 'idle'-Trigger — feuert wenn die Map
    // wirklich fertig geladen ist (Style + Sources + erste Tile-Renders).
    // Garantierter Endpunkt der Init-Sequenz, sehr zuverlässig.
    const onIdle = () => applyOnce('idle');
    map.on('idle', onIdle);
    // sofort versuchen falls Style + Features schon ready sind
    applyOnce('mount');

    return () => {
      cancelled = true;
      try {
        map.off('styledata', onStyleData);
        map.off('idle', onIdle);
      } catch {
        /* map already destroyed */
      }
      removePoiSymbol(map);
    };
  }, [drinkingWaterOn, mapReady]);


  // ---------------------------------------------------------------------
  // Stroke-Source (nur stroke-Mode)
  // ---------------------------------------------------------------------
  const renderedStrokes = mode === 'stroke' ? strokes : ([] as Stroke[]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource(STROKES_DONE_SOURCE) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: renderedStrokes.map((s) => ({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: s },
      })),
    });
  }, [renderedStrokes, mapReady, styleVersion]);

  // ---------------------------------------------------------------------
  // Trails-Cache + Source
  // ---------------------------------------------------------------------
  useEffect(() => {
    const parser = new DOMParser();
    let cancelled = false;
    syncTrailCache({
      domParser: parser,
      onProgress: (trail) => {
        if (cancelled) return;
        setTrails((prev) => {
          const idx = prev.findIndex((t) => t.id === trail.id);
          if (idx < 0) return [...prev, trail];
          const next = prev.slice();
          next[idx] = trail;
          return next;
        });
      },
    })
      .then((list) => {
        if (!cancelled) setTrails(list);
      })
      .catch((err) => console.warn('[createCustom] syncTrailCache failed', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource(TRAILS_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const features: Array<GeoJSON.Feature<GeoJSON.LineString>> = [];
    const endpointFeatures: Array<GeoJSON.Feature<GeoJSON.Point>> = [];
    for (const t of trails) {
      if (!t.track || !t.track.points || t.track.points.length < 2) continue;
      // Stage 6m: Difficulty-Filter aus dem trailFilterBus. Wenn der User
      // in der DifficultyLegend Trails ausschliesst, werden die hier nicht
      // mehr gezeichnet — synchron mit dem Verhalten auf MapPage.
      // Wave 14eh-fix: hideAll ("None"-Preset) → gar nichts rendern.
      if (hideAllTrails) continue;
      if (diffFilter.size > 0 && !diffFilter.has(t.difficulty)) continue;
      const f = trackToGeoJson(t.track);
      // Wave 14ca-fix64: resolveTrailStyle liefert isDashed + hasUndercoat
      // damit Stripe-Difficulties (extreme: schwarz/weiss, intermediate_plus:
      // blau/rot) hier genauso aussehen wie auf der Hauptkarte. Vorher hat
      // CreateCustomPage einfach DIFFICULTY_COLOR[difficulty] als solide
      // Linie gesetzt — extreme wurde dadurch als solid schwarz gerendert
      // und nicht von expert (auch schwarz) zu unterscheiden.
      const resolved = resolveTrailStyle(t.difficulty, t.colorHex);
      f.properties = {
        id: t.id,
        name: t.name,
        lineColor: resolved.lineColor,
        isDashed: resolved.isDashed,
        hasUndercoat: resolved.hasUndercoat,
      };
      (f as GeoJSON.Feature & { id?: string }).id = t.id;
      features.push(f);
      const coords = f.geometry.coordinates;
      const start = coords[0];
      const end = coords[coords.length - 1];
      if (start) {
        endpointFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: start },
          properties: { role: 'start', trailId: t.id },
        });
      }
      if (end) {
        endpointFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: end },
          properties: { role: 'end', trailId: t.id },
        });
      }
    }
    src.setData({ type: 'FeatureCollection', features });
    const epSrc = map.getSource(TRAIL_ENDPOINTS_SOURCE) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (epSrc) {
      epSrc.setData({ type: 'FeatureCollection', features: endpointFeatures });
    }
  }, [trails, mapReady, styleVersion, diffFilter, hideAllTrails]);

  // Highlight-Filter synchronisieren wenn sich die Auswahl ändert.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!map.getLayer(TRAILS_SELECTED_LAYER)) return;
    // `['in', ['get', 'id'], ['literal', [...ids]]]` — Expression, nicht
    // Legacy-Filter (der kann keine Arrays dynamisch).
    map.setFilter(TRAILS_SELECTED_LAYER, [
      'in',
      ['get', 'id'],
      ['literal', selectedTrailIds],
    ]);
  }, [selectedTrailIds, mapReady, styleVersion]);

  // ---------------------------------------------------------------------
  // Route-Source
  // ---------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource(ROUTE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const coords = route?.resp.coordinates ?? [];
    if (!coords.length) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    // Stage 6m-fix4: pro Route-Segment ein Feature mit Farbe in den
    // Properties. Trail-Segmente bekommen die Difficulty-Farbe (matched zu
    // den auf der Map sichtbaren Trails — visueller Tie-In). Connector-
    // Segmente bleiben in Sky-Blue. Bridge-Segmente kriegen `kind: 'bridge'`
    // und werden vom separaten gestrichelten Layer in Rot gerendert.
    //
    // Fallback: wenn das Backend `routeSegments` nicht liefert (alte API),
    // rendern wir die ganze Route als ein einziges Connector-Feature
    // (Sky-Blue) — das war's vorher auch.
    const trailById = new Map<string, CachedTrail>();
    for (const t of trails) trailById.set(t.id, t);

    const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
    const routeSegments = route?.kind === 'pin' ? route.resp.routeSegments : null;

    if (routeSegments && routeSegments.length > 0) {
      for (const seg of routeSegments) {
        if (seg.from < 0 || seg.to >= coords.length || seg.to <= seg.from) continue;
        const segCoords = coords.slice(seg.from, seg.to + 1);
        let color: string;
        if (seg.kind === 'trail' && seg.trailId) {
          const t = trailById.get(seg.trailId);
          color = t?.colorHex || (t ? DIFFICULTY_COLOR[t.difficulty] : '#38bdf8') || '#38bdf8';
        } else if (seg.kind === 'bridge') {
          color = '#ef4444';
        } else {
          color = '#38bdf8';
        }
        features.push({
          type: 'Feature',
          properties: {
            kind: seg.kind,
            trailId: seg.trailId ?? null,
            color,
          },
          geometry: { type: 'LineString', coordinates: segCoords as [number, number][] },
        });
      }
    } else {
      // Legacy single-feature fallback
      features.push({
        type: 'Feature',
        properties: { kind: 'connector', trailId: null, color: '#38bdf8' },
        geometry: { type: 'LineString', coordinates: coords },
      });
    }

    src.setData({ type: 'FeatureCollection', features });

    const bounds = new maplibregl.LngLatBounds();
    for (const c of coords) bounds.extend(c as [number, number]);
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 60, duration: 700, maxZoom: 15 });
    }
  }, [route, mapReady, styleVersion, trails]);

  // ---------------------------------------------------------------------
  // Touch-Handling
  // ---------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const container = map.getContainer();

    const lngLatFromTouch = (t: Touch): [number, number] | null => {
      const rect = container.getBoundingClientRect();
      const x = t.clientX - rect.left;
      const y = t.clientY - rect.top;
      try {
        const ll = map.unproject([x, y]);
        return [ll.lng, ll.lat];
      } catch {
        return null;
      }
    };

    const updateLiveSource = () => {
      const src = map.getSource(STROKES_LIVE_SOURCE) as
        | maplibregl.GeoJSONSource
        | undefined;
      if (!src) return;
      const cur = currentStrokeRef.current;
      src.setData({
        type: 'FeatureCollection',
        features:
          cur && cur.length > 0
            ? [
                {
                  type: 'Feature',
                  properties: {},
                  geometry: { type: 'LineString', coordinates: cur },
                },
              ]
            : [],
      });
    };

    const cancelCurrentStroke = () => {
      if (currentStrokeRef.current) {
        currentStrokeRef.current = null;
        lastPointPxRef.current = null;
        updateLiveSource();
      }
    };

    const averageTouchPx = (touches: TouchList): [number, number] | null => {
      if (touches.length === 0) return null;
      const rect = container.getBoundingClientRect();
      let sx = 0;
      let sy = 0;
      for (let i = 0; i < touches.length; i++) {
        const t = touches[i]!;
        sx += t.clientX - rect.left;
        sy += t.clientY - rect.top;
      }
      return [sx / touches.length, sy / touches.length];
    };

    // Stage 6i: Pixel-Distanz zwischen den ersten beiden Fingern, für
    // Pinch-Zoom-Berechnung. Liefert 0 bei <2 Fingern (caller ignoriert).
    const twoFingerDistPx = (touches: TouchList): number => {
      if (touches.length < 2) return 0;
      const rect = container.getBoundingClientRect();
      const a = touches[0]!;
      const b = touches[1]!;
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.hypot(dx, dy);
    };

    // In welcher Phase sind wir gerade? Steuert was ein Tap bedeutet.
    const pinsPhase = (): 'start' | 'end' | 'trails' => {
      if (!startPinRef.current) return 'start';
      if (!endPinRef.current) return 'end';
      return 'trails';
    };

    // Draw-Kontext — Stroke-Mode immer aktiv; Pins-Mode nie.
    const canDrawNow = () => modeRef.current === 'stroke';

    // Wave 14ea/ed: Auf Desktop ist der ganze Mouse-Drag fuer MapLibre's
    // dragPan reserviert (sonst kann der User die Karte gar nicht
    // verschieben — Wave 14dy). Damit man trotzdem zeichnen kann, gilt
    // im Stroke-Mode auf Desktop: nur wenn die T-Taste gerade gehalten
    // wird, wird gemalt — sonst pannt's. T wurde nach mehreren Fehl-
    // versuchen mit ALT/Option/Shift gewaehlt: ALT wird auf Mac von
    // MapLibre's dragRotate gekapert, Shift triggert boxZoom. T ist
    // semantisch (= "Tour") und kollidiert mit nix.
    // Auf Touch (coarse pointer) keine Modifier-Voraussetzung — da
    // gibt's eh kein dragPan, der Single-Finger bleibt fuer Strokes.
    const canDrawWithMouse = (_e: MouseEvent) => {
      if (!canDrawNow()) return false;
      if (isCoarsePointerRef.current) return true;
      return tKeyDownRef.current;
    };

    // Trail unter einem Screen-Pixel finden — gibt das Feature mit der
    // kürzesten projizierten Distanz im Tolerance-Quadrat zurück.
    const findTrailAt = (px: [number, number]): string | null => {
      // Wave 14ca-fix64: Layer-Liste auf SOLID + DASHED + HALO erweitert,
      // weil der frueher singulaere TRAILS_LINE_LAYER durch die Stripe-
      // Pipeline ersetzt wurde. HALO ist als Fallback dabei — er rendert
      // immer, auch wenn die Mainline durch ein Filter ausgeblendet sein
      // sollte.
      if (
        !map.getLayer(TRAILS_LINE_SOLID_LAYER) &&
        !map.getLayer(TRAILS_LINE_DASHED_LAYER)
      ) {
        return null;
      }
      const tol = TRAIL_TAP_TOLERANCE_PX;
      const bbox: [[number, number], [number, number]] = [
        [px[0] - tol, px[1] - tol],
        [px[0] + tol, px[1] + tol],
      ];
      const feats = map.queryRenderedFeatures(bbox, {
        layers: [
          TRAILS_LINE_SOLID_LAYER,
          TRAILS_LINE_DASHED_LAYER,
          TRAILS_HALO_LAYER,
        ],
      });
      if (!feats || feats.length === 0) return null;
      // Falls mehrere Trails im Tolerance-Bereich: den ersten nehmen —
      // MapLibre sortiert grob nach Render-Reihenfolge, d.h. der
      // sichtbarste zuerst. In den seltenen Fällen wo sich Trails fast
      // überlappen, kann der User einfach nochmal reinzoomen und präziser
      // tippen.
      for (const f of feats) {
        const id = (f.properties && (f.properties as { id?: string }).id) || null;
        if (typeof id === 'string' && id) return id;
      }
      return null;
    };

    const toggleTrailSelection = (trailId: string) => {
      // Wave 14ea: NICHT mehr ueber setSelectedTrailIds gehen — der Helper
      // sortiert Trails vor Vias um (legacy-Verhalten) und zerschiesst
      // damit die Tap-Reihenfolge. User-Bug-Report: "wenn ich zB zuerst
      // einen VIA punkt setze und DANN einen trail, setzt er mir den
      // trail VOR den VIA punkt". Wir manipulieren stattdessen den
      // waypoints-Array direkt, damit Trails und Vias in echter Tap-
      // Reihenfolge bleiben (z. B. via1 → trail1 → trail2 → via2 ...).
      setWaypoints((prev) => {
        const existingIdx = prev.findIndex(
          (w) => w.kind === 'trail' && w.trailId === trailId,
        );
        if (existingIdx >= 0) {
          // Toggle off: Trail rausnehmen, Reihenfolge der Restelemente
          // bleibt unangetastet.
          const next = prev.slice();
          next.splice(existingIdx, 1);
          return next;
        }
        // Append am Ende — das ist die echte Tap-Reihenfolge.
        return [...prev, { kind: 'trail', trailId }];
      });
      setRoute(null);
      setErrorMsg(null);
    };

    // Stage 6m: Tap auf einen existierenden Via-Pin entfernt ihn (Toggle).
    // Radius in Screen-Pixeln; verschiedene Vias dürfen sich nicht
    // überlappen, also reicht ein moderater Schwellwert.
    const VIA_TAP_TOLERANCE_PX = 22;
    const findViaAt = (px: [number, number]): string | null => {
      const all = waypointsRef.current.filter(
        (w): w is Extract<Waypoint, { kind: 'via' }> => w.kind === 'via',
      );
      let bestId: string | null = null;
      let bestD2 = VIA_TAP_TOLERANCE_PX * VIA_TAP_TOLERANCE_PX;
      for (const v of all) {
        try {
          const p = map.project(v.lonLat);
          const dx = p.x - px[0];
          const dy = p.y - px[1];
          const d2 = dx * dx + dy * dy;
          if (d2 < bestD2) {
            bestD2 = d2;
            bestId = v.id;
          }
        } catch {
          /* ignore unproject errors */
        }
      }
      return bestId;
    };

    const handlePinsTap = (px: [number, number], ll: [number, number]) => {
      const phase = pinsPhase();
      if (phase === 'start') {
        setStartPin(ll);
        probeSnapRef.current?.('start', ll);
        // Wave 14eh: loop-pin → der EINE Tap setzt Start UND End gleich,
        // damit der User direkt in die Trails-Phase springt ohne nochmal
        // tappen zu muessen.
        if (planModeRef.current === 'loop-pin') {
          setEndPin(ll);
          probeSnapRef.current?.('end', ll);
        }
        setRoute(null);
        setErrorMsg(null);
        return;
      }
      if (phase === 'end') {
        setEndPin(ll);
        probeSnapRef.current?.('end', ll);
        setRoute(null);
        setErrorMsg(null);
        return;
      }
      // trails-Phase: drei Möglichkeiten in Priorität:
      //   1) Tap auf einen existierenden Via → Via entfernen
      //   2) Tap auf einen Trail → Trail-Auswahl togglen
      //   3) Tap auf leere Karte → neuen Via droppen (Stage 6m)
      const viaId = findViaAt(px);
      if (viaId) {
        setWaypoints((prev) =>
          prev.filter((w) => !(w.kind === 'via' && w.id === viaId)),
        );
        setRoute(null);
        setErrorMsg(null);
        return;
      }
      const tid = findTrailAt(px);
      if (tid) {
        toggleTrailSelection(tid);
        return;
      }
      // Stage 6m: leere Karte = Via droppen, in Tap-Reihenfolge ANS ENDE
      // der Waypoint-Liste. Damit folgt der Via-Pin nach allen aktuell
      // selektierten Trails in der Route. Reorder-UI können wir später
      // dazubauen wenn der Bedarf da ist.
      const newViaId = `via-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setWaypoints((prev) => [
        ...prev,
        {
          kind: 'via',
          lonLat: ll,
          id: newViaId,
        },
      ]);
      // Wave 14eq: Snap-Probe auch fuer Via-Pins.
      probeSnapRef.current?.(`via-${newViaId}`, ll);
      setRoute(null);
      setErrorMsg(null);
    };

    // Stage 6m-fix: Taps auf MapLibre-Control-Buttons (z.B. das Locate-
    // Icon oben rechts) dürfen NICHT als Map-Tap interpretiert werden,
    // sonst setzt der Phase-Handler ungewollt einen Start-/End-Pin.
    // User-Bug: "wenn ich auf den icon klicke um meine aktuelle position
    // zu bestimmen, wird schon direkt ein startpunkt gesetzt".
    //
    // Trick: wenn das Tap-Target Element innerhalb eines `.maplibregl-ctrl`
    // liegt, lassen wir das Event durch ohne touchStartPxRef zu setzen —
    // damit ist `wasTap` in onTouchEnd false und handlePinsTap wird nicht
    // gecallt. Der Control selbst (z.B. LocateControl-Button) feuert sein
    // eigenes click-Event und macht seine Sache.
    //
    // Wave 14ct: gleiches Problem für DOM-Marker (Battery-Stations). Vorher
    // hat der container-Touch-Handler den Tap auf einer Station-Pille
    // gefressen — preventDefault auf touchstart hat das synthetische click
    // unterdrückt, sodass die Marker-onClick-Callback (→ setEndPin auf die
    // Station) nie kam. Stattdessen wurde der Tap als Map-Tap interpretiert
    // und ein normaler Pin an der Tap-Stelle gesetzt. Jetzt: maplibregl-
    // marker liegt im selben Bail-Out wie maplibregl-ctrl.
    const isOnMapLibreInteractive = (target: EventTarget | null): boolean => {
      if (!(target instanceof Element)) return false;
      // maplibregl-marker fängt alle DOM-Marker (Battery-Stations etc.).
      // adt-battery-station-marker ist redundant — wir setzen die Klasse
      // selbst im BatteryStationLayer, dient als Fallback falls eine künftige
      // maplibre-Version die Default-Klasse umbenennt.
      return !!target.closest(
        '.maplibregl-ctrl, .maplibregl-marker, .adt-battery-station-marker',
      );
    };
    // Alias für die alten Call-Sites — semantisch passt der neue Name
    // besser ("interactive child of map container"), aber refactoring der
    // Mouse-Handler unten wäre zusätzliches Risiko. Funktionell identisch.
    const isOnMapLibreControl = isOnMapLibreInteractive;

    const onTouchStart = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        activeTouchIdsRef.current.add(e.changedTouches[i]!.identifier);
      }

      if (activeTouchIdsRef.current.size >= 2) {
        cancelCurrentStroke();
        touchStartPxRef.current = null;
        twoFingerCenterPxRef.current = averageTouchPx(e.touches);
        twoFingerDistPxRef.current = twoFingerDistPx(e.touches) || null;
        return;
      }

      // Bail out wenn der Tap auf einem MapLibre-Control liegt (Locate,
      // Zoom-Buttons etc.). Kein touchStartPxRef setzen, kein preventDefault
      // — der Control bekommt sein click-Event normal.
      if (isOnMapLibreControl(e.target)) {
        return;
      }

      if (activeTouchIdsRef.current.size === 1 && e.touches.length === 1) {
        const t = e.touches[0]!;
        const rect = container.getBoundingClientRect();
        const px: [number, number] = [t.clientX - rect.left, t.clientY - rect.top];
        touchStartPxRef.current = px;
        touchStartTimeRef.current = Date.now();
        movedBeyondTapRef.current = false;

        if (canDrawNow()) {
          const ll = lngLatFromTouch(t);
          if (!ll) return;
          e.preventDefault();
          currentStrokeRef.current = [ll];
          lastPointPxRef.current = px;
          updateLiveSource();
        } else {
          // pins-Modus: preventDefault damit Browser nicht pannt/scrollt.
          e.preventDefault();
        }
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        cancelCurrentStroke();
        touchStartPxRef.current = null;

        // Stage 6i: preventDefault damit iOS/Chrome den Pinch nicht als
        // Page-Zoom interpretieren. passive: false ist oben registriert.
        e.preventDefault();

        const newC = averageTouchPx(e.touches);
        const prevC = twoFingerCenterPxRef.current;

        // Pinch-Zoom: Ratio der aktuellen Finger-Distanz zur vorigen.
        // zoomDelta = log2(ratio) — so wird 2× Finger-Abstand = +1 Zoom-
        // Level (genau wie iOS Maps).
        const newDist = twoFingerDistPx(e.touches);
        const prevDist = twoFingerDistPxRef.current;
        if (prevDist && newDist && prevDist > 0 && newDist > 0) {
          const ratio = newDist / prevDist;
          // Kleine Wackler ignorieren (Rauschen im Touch-Sensor).
          if (Math.abs(ratio - 1) > 0.003) {
            const zoomDelta = Math.log2(ratio);
            const currentZoom = map.getZoom();
            // Optional: zoom um den Mittelpunkt der Finger zentrieren statt
            // um den Map-Center. Das entspricht dem iOS-Maps-Gefühl. Wir
            // nutzen map.easeTo mit around: mittelpunkt-in-LngLat.
            let around: maplibregl.LngLat | null = null;
            if (newC) {
              try {
                around = map.unproject([newC[0], newC[1]]);
              } catch {
                /* fallback: map.setZoom ohne around */
              }
            }
            if (around) {
              map.easeTo({
                zoom: currentZoom + zoomDelta,
                around,
                duration: 0,
              });
            } else {
              map.setZoom(currentZoom + zoomDelta);
            }
          }
        }
        twoFingerDistPxRef.current = newDist || twoFingerDistPxRef.current;

        // Pan via Mittelpunkts-Delta (wie bisher — geht parallel zum Zoom
        // für ein natürliches Gefühl).
        if (newC && prevC) {
          const dx = newC[0] - prevC[0];
          const dy = newC[1] - prevC[1];
          if (dx !== 0 || dy !== 0) {
            map.panBy([-dx, -dy], { duration: 0 });
          }
        }
        twoFingerCenterPxRef.current = newC;
        return;
      }

      if (e.touches.length !== 1) return;

      const t = e.touches[0]!;
      const rect = container.getBoundingClientRect();
      const px = t.clientX - rect.left;
      const py = t.clientY - rect.top;

      const ts = touchStartPxRef.current;
      if (ts) {
        const mdx = px - ts[0];
        const mdy = py - ts[1];
        if (mdx * mdx + mdy * mdy > TAP_MAX_PX * TAP_MAX_PX) {
          movedBeyondTapRef.current = true;
        }
      }

      if (!currentStrokeRef.current) return;

      const last = lastPointPxRef.current;
      if (last) {
        const dx = px - last[0];
        const dy = py - last[1];
        if (dx * dx + dy * dy < MIN_POINT_PX * MIN_POINT_PX) return;
      }

      const ll = lngLatFromTouch(t);
      if (!ll) return;

      e.preventDefault();
      currentStrokeRef.current.push(ll);
      lastPointPxRef.current = [px, py];
      updateLiveSource();
    };

    const finalizeDrawStroke = () => {
      const cur = currentStrokeRef.current;
      if (cur && cur.length >= 2 && modeRef.current === 'stroke') {
        setStrokes((prev) => [...prev, cur]);
        setRoute(null);
        setErrorMsg(null);
      }
      currentStrokeRef.current = null;
      lastPointPxRef.current = null;
      updateLiveSource();
    };

    const onTouchEnd = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        activeTouchIdsRef.current.delete(e.changedTouches[i]!.identifier);
      }

      if (activeTouchIdsRef.current.size >= 2) {
        // Finger-Release während 2+ Finger noch aktiv — Mittelpunkt UND
        // Distanz neu snappen, damit der nächste Move kein Phantom-Delta
        // produziert (das würde einen Pan- oder Zoom-Sprung verursachen).
        twoFingerCenterPxRef.current = averageTouchPx(e.touches);
        twoFingerDistPxRef.current = twoFingerDistPx(e.touches) || null;
        return;
      }
      twoFingerCenterPxRef.current = null;
      twoFingerDistPxRef.current = null;

      if (activeTouchIdsRef.current.size > 0) return;

      const dt = Date.now() - touchStartTimeRef.current;
      const wasTap =
        touchStartPxRef.current !== null &&
        !movedBeyondTapRef.current &&
        dt < TAP_MAX_MS;

      if (wasTap && modeRef.current === 'pins') {
        const px = touchStartPxRef.current!;
        let ll: [number, number] | null = null;
        try {
          const l = map.unproject([px[0], px[1]]);
          ll = [l.lng, l.lat];
        } catch {
          /* ignore */
        }
        const ct = e.changedTouches[0];
        if (!ll && ct) ll = lngLatFromTouch(ct);
        if (ll) handlePinsTap(px, ll);
        currentStrokeRef.current = null;
        lastPointPxRef.current = null;
        touchStartPxRef.current = null;
        return;
      }

      finalizeDrawStroke();
      touchStartPxRef.current = null;
    };

    const onTouchCancel = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        activeTouchIdsRef.current.delete(e.changedTouches[i]!.identifier);
      }
      twoFingerCenterPxRef.current = null;
      twoFingerDistPxRef.current = null;
      touchStartPxRef.current = null;
      cancelCurrentStroke();
    };

    // Desktop-Maus.
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      // Stage 6m-fix: gleiche Control-Tap-Guard wie im Touch-Handler.
      if (isOnMapLibreControl(e.target)) return;
      const rect = container.getBoundingClientRect();
      const px: [number, number] = [e.clientX - rect.left, e.clientY - rect.top];
      touchStartPxRef.current = px;
      touchStartTimeRef.current = Date.now();
      movedBeyondTapRef.current = false;
      if (canDrawWithMouse(e)) {
        const ll = pxToLngLat(map, px);
        if (!ll) return;
        // Stroke-Mode (Touch immer / Desktop nur mit ALT): blockieren,
        // sonst kollidiert das Zeichnen mit dragPan. stopPropagation
        // zusätzlich, damit MapLibre's interne Listener (auch wenn
        // dragRotate disabled ist, kann pitch oder anderes greifen)
        // den Stroke nicht mitfangen.
        e.preventDefault();
        e.stopPropagation();
        currentStrokeRef.current = [ll];
        lastPointPxRef.current = px;
        updateLiveSource();
      } else if (isCoarsePointerRef.current) {
        // Wave 14dy: preventDefault nur auf Touch-Geraeten (mit altem
        // synthetischen Mouse-Event). Auf Desktop NICHT — sonst
        // schluckt das Event den nativen dragPan von MapLibre und der
        // Nutzer kann die Karte nicht mehr ziehen. Pin-Taps funktionieren
        // weiter ueber die wasTap-Detection im onMouseUp; sobald die
        // Bewegung > TAP_MAX_PX ist, war's ein Drag (= MapLibre hat schon
        // gepannt) und es wird kein Pin gesetzt.
        e.preventDefault();
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!touchStartPxRef.current) return;
      const rect = container.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const ts = touchStartPxRef.current;
      const mdx = px - ts[0];
      const mdy = py - ts[1];
      if (mdx * mdx + mdy * mdy > TAP_MAX_PX * TAP_MAX_PX) {
        movedBeyondTapRef.current = true;
      }
      if (!currentStrokeRef.current) return;
      const last = lastPointPxRef.current;
      if (last) {
        const dx = px - last[0];
        const dy = py - last[1];
        if (dx * dx + dy * dy < MIN_POINT_PX * MIN_POINT_PX) return;
      }
      const ll = pxToLngLat(map, [px, py]);
      if (!ll) return;
      // Wave 14ec: stopPropagation auch hier, damit MapLibre den Move
      // nicht parallel als Pan/Pitch behandelt waehrend wir zeichnen.
      e.preventDefault();
      e.stopPropagation();
      currentStrokeRef.current.push(ll);
      lastPointPxRef.current = [px, py];
      updateLiveSource();
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!touchStartPxRef.current) return;
      const dt = Date.now() - touchStartTimeRef.current;
      const wasTap = !movedBeyondTapRef.current && dt < TAP_MAX_MS;
      if (wasTap && modeRef.current === 'pins') {
        const rect = container.getBoundingClientRect();
        const px: [number, number] = [e.clientX - rect.left, e.clientY - rect.top];
        const ll = pxToLngLat(map, px);
        if (ll) handlePinsTap(px, ll);
        currentStrokeRef.current = null;
        lastPointPxRef.current = null;
        touchStartPxRef.current = null;
        return;
      }
      finalizeDrawStroke();
      touchStartPxRef.current = null;
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    container.addEventListener('touchcancel', onTouchCancel);
    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseup', onMouseUp);

    // Wave 14ed: Document-level T-Key-Listener fuer Draw-Modifier.
    // Wir lauschen aufs Dokument, nicht auf den Container, weil der
    // Container nur Focus bekommt wenn der User reinklickt — der User
    // soll aber auch T DRUECKEN BEVOR er klickt, um den Draw-Cursor
    // bereitzuhaben. Eingabe in Input/Textarea ignorieren, damit ein
    // Save-Name-Modal-Tippe-T nicht den Modifier triggert.
    const isTypingInField = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (el as HTMLElement).isContentEditable
      );
    };
    // Wave 14ee: dragPan dynamisch ein/aus schalten je nachdem ob T
    // gehalten wird. `preventDefault` allein reicht nicht — MapLibre's
    // Drag wird beim mousedown auf dem Canvas initiiert und ueberlebt
    // unser preventDefault. Hier disablen wir den ganzen Pan-Handler
    // waehrend T gedrueckt ist, damit kein Konkurrenz-Verhalten mehr
    // moeglich ist. Plus Crosshair-Cursor als visuelles Feedback.
    const setDrawCursor = (on: boolean) => {
      const canvas = mapRef.current?.getCanvas();
      if (canvas) canvas.style.cursor = on ? 'crosshair' : '';
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 't' && e.key !== 'T') return;
      if (isTypingInField()) return;
      if (tKeyDownRef.current) return; // key-repeat ignorieren
      tKeyDownRef.current = true;
      if (!isCoarsePointerRef.current && mapRef.current) {
        try {
          mapRef.current.dragPan.disable();
        } catch {
          /* ignore */
        }
        setDrawCursor(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 't' && e.key !== 'T') return;
      tKeyDownRef.current = false;
      if (!isCoarsePointerRef.current && mapRef.current) {
        try {
          mapRef.current.dragPan.enable();
        } catch {
          /* ignore */
        }
        setDrawCursor(false);
      }
      // Falls der User mitten im Zeichnen die T-Taste loslaesst:
      // Stroke sauber abschliessen (sonst haengt der currentStrokeRef
      // weiter und der naechste Click ohne T wuerde da reinhaengen).
      if (currentStrokeRef.current) {
        finalizeDrawStroke();
        touchStartPxRef.current = null;
      }
    };
    // Blur: Browser-Tab-Switch verliert keyup → Ref + dragPan wieder
    // zuruecksetzen, sonst klebt der Draw-Modifier fest UND dragPan
    // bleibt disabled.
    const onBlur = () => {
      tKeyDownRef.current = false;
      if (!isCoarsePointerRef.current && mapRef.current) {
        try {
          mapRef.current.dragPan.enable();
        } catch {
          /* ignore */
        }
        setDrawCursor(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchCancel);
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      activeTouchIdsRef.current.clear();
      currentStrokeRef.current = null;
      lastPointPxRef.current = null;
      twoFingerCenterPxRef.current = null;
      twoFingerDistPxRef.current = null;
      touchStartPxRef.current = null;
      tKeyDownRef.current = false;
    };
  }, [mapReady]);

  // Tap-miss Flash nach 1.5s ausblenden.
  useEffect(() => {
    if (tapMissedAt == null) return;
    const t = setTimeout(() => setTapMissedAt(null), 1500);
    return () => clearTimeout(t);
  }, [tapMissedAt]);

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------
  const handleUndo = useCallback(() => {
    if (mode === 'stroke') {
      setStrokes((prev) => prev.slice(0, -1));
    } else {
      // Wave 14en: Pins-Mode-Undo geht jetzt durch den GESAMTEN Stack
      // an User-Aktionen rueckwaerts, nicht nur ueber Trail-Picks. Reihen-
      // folge: letzter Waypoint (Trail oder Via) → End-Pin → Start-Pin.
      // User-Bug-Report: "undo sollte niemals ausgegraut sein, falls wie
      // hier der letzte pin nicht auf einer strasse liegt moechte ich
      // ranzoomen und ihn neu setzen". Sonst musste man fuer einen
      // verrutschten End-Pin den ganzen Clear-Button druecken und wieder
      // von vorne anfangen.
      if (waypointsRef.current.length > 0) {
        setWaypoints((prev) => prev.slice(0, -1));
      } else if (endPinRef.current) {
        setEndPin(null);
      } else if (startPinRef.current) {
        setStartPin(null);
      }
    }
    setRoute(null);
    setErrorMsg(null);
  }, [mode]);

  const handleClear = useCallback(() => {
    setStrokes([]);
    setStartPin(null);
    setEndPin(null);
    // Stage 6m-fix: setSelectedTrailIds([]) leerte nur Trails, Vias blieben
    // — User-Bug-Report ("CLEAR ... der gelbe via-kreis bleibt"). Direkt
    // setWaypoints([]) wipet beides.
    setWaypoints([]);
    setSnapResults({}); // Wave 14eq: Snap-Feedback-Ringe + Strassen weg.
    setRoute(null);
    setErrorMsg(null);
  }, []);

  const handleResetStart = useCallback(() => {
    setStartPin(null);
    setSnapResults((prev) => {
      const next = { ...prev };
      delete next.start;
      return next;
    });
    setRoute(null);
    setErrorMsg(null);
  }, []);

  const handleResetEnd = useCallback(() => {
    setEndPin(null);
    setSnapResults((prev) => {
      const next = { ...prev };
      delete next.end;
      return next;
    });
    setRoute(null);
    setErrorMsg(null);
  }, []);

  const handleRemoveTrail = useCallback((trailId: string) => {
    setSelectedTrailIds((prev) => prev.filter((t) => t !== trailId));
    setRoute(null);
    setErrorMsg(null);
  }, []);

  const handleSwitchMode = useCallback((next: Mode) => {
    setMode((prev) => {
      if (prev === next) return prev;
      setStrokes([]);
      setStartPin(null);
      setEndPin(null);
      // Mode-Switch wipet alles — auch Vias.
      setWaypoints([]);
      setRoute(null);
      setErrorMsg(null);
      return next;
    });
  }, []);

  /**
   * Stage 6m — aktuelle GPS-Position holen (one-shot). Gibt [lng, lat] oder
   * null bei Fehler. Nutzt navigator.geolocation direkt, NICHT den
   * positionBus — in CreateCustomPage läuft kein LocateControl, der Bus
   * wäre leer. High-accuracy + 10s Timeout ist angemessen für den typischen
   * "ich stehe draussen und will jetzt planen"-Use-Case.
   */
  const acquireCurrentPosition = useCallback((): Promise<
    [number, number] | null
  > => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setGpsError('Geolocation not supported on this device.');
        resolve(null);
        return;
      }
      setGpsBusy(true);
      setGpsError(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGpsBusy(false);
          resolve([pos.coords.longitude, pos.coords.latitude]);
        },
        (err) => {
          setGpsBusy(false);
          // PERMISSION_DENIED=1, POSITION_UNAVAILABLE=2, TIMEOUT=3
          const msg =
            err.code === 1
              ? 'Location permission denied.'
              : err.code === 3
                ? 'Location request timed out.'
                : 'Could not get your location.';
          setGpsError(msg);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
      );
    });
  }, []);

  /**
   * Stage 6m / Wave 14bz — Planungsmodus wählen.
   *   - from-here: GPS-Fix → startPin
   *   - pin-to-pin: nix automatisch
   *   - multi-pin: alles zurücksetzen, User tappt Pins der Reihe nach
   */
  const handlePickPlanMode = useCallback(
    async (next: PlanMode) => {
      setPlanMode(next);
      setMode('pins');
      // Reset wenn Mode-Wechsel.
      if (planModeSetRef.current && planModeSetRef.current !== next) {
        setStartPin(null);
        setEndPin(null);
        // Plan-Mode-Switch wipet alles — auch Vias.
        setWaypoints([]);
        setRoute(null);
        setErrorMsg(null);
      }
      planModeSetRef.current = next;

      if (next === 'from-here' || next === 'loop-here') {
        const pos = await acquireCurrentPosition();
        if (!pos) return; // Error wird oben schon im State angezeigt.
        // Wave 14eh: Out-of-region-Guard (analog handleUseMyPosition).
        const [lon, lat] = pos;
        if (!isInsideAnyRegion(lat, lon)) {
          setErrorMsg(
            `Your current location is outside our supported regions (${regionNamesList()}). Pick "From one point to another" or "Loop from a point" instead.`,
          );
          return;
        }
        setStartPin(pos);
        probeSnapRef.current?.('start', pos);
        // Loop-here: gleicher Punkt fuer Start UND Ziel — so geht die
        // Route am Ende wieder zurueck zur User-Position.
        if (next === 'loop-here') {
          setEndPin(pos);
          probeSnapRef.current?.('end', pos);
        }
        // Stage 6m-fix: Map auch direkt zur User-Position fliegen, sonst
        // sieht man nur den Pin am Rand und muss manuell schubsen.
        const map = mapRef.current;
        if (map) {
          try {
            map.flyTo({
              center: pos,
              zoom: 14,
              duration: 800,
            });
          } catch (err) {
            console.warn('[create-custom] flyTo nach GPS-Fix fehlgeschlagen', err);
          }
        }
      }
      // 'loop-pin': User tappt EINEN Punkt, wir setzen Start UND End
      // dort hin — Behandlung passiert im handlePinsTap (s. nachstehende
      // useEffect / pinsPhase-Logik in der Datei).
    },
    [acquireCurrentPosition],
  );

  /**
   * Stage 6m — "Meine Position"-Button. Holt die aktuelle GPS-Position und
   * setzt sie auf den NÄCHSTEN fehlenden Pin (Start wenn leer, sonst End).
   */
  const handleUseMyPosition = useCallback(async () => {
    const pos = await acquireCurrentPosition();
    if (!pos) return;
    // Wave 14ef: Out-of-region-Guard. Wenn die GPS-Position des Users
    // ausserhalb aller unterstuetzten Regionen liegt (z. B. Tester in
    // Deutschland), platzieren wir KEINEN Pin und sagen freundlich
    // warum. Sonst kaeme ein kryptischer Routing-Fehler vom Backend
    // ("start_not_snapped") wenn der User auf "Calculate" druckt.
    const [lon, lat] = pos;
    if (!isInsideAnyRegion(lat, lon)) {
      setErrorMsg(
        `Your current location is outside our supported regions (${regionNamesList()}). Drop a pin on the map instead — or zoom into Liguria / Elba / Finale Outdoor and tap there.`,
      );
      return;
    }
    // pin-to-pin / from-here: Start zuerst, dann End.
    if (!startPin) {
      setStartPin(pos);
      probeSnapRef.current?.('start', pos);
    } else if (!endPin) {
      setEndPin(pos);
      probeSnapRef.current?.('end', pos);
    } else {
      // Beide schon gesetzt — Start überschreiben (häufigster Wunsch).
      setStartPin(pos);
      probeSnapRef.current?.('start', pos);
    }
    // Stage 6m-fix: Map zur Position fliegen damit der frisch gesetzte
    // Pin sofort sichtbar ist (oneShot, kein Auto-Follow danach).
    const map = mapRef.current;
    if (map) {
      try {
        map.flyTo({ center: pos, zoom: 14, duration: 800 });
      } catch (err) {
        console.warn('[create-custom] flyTo nach GPS-Fix fehlgeschlagen', err);
      }
    }
  }, [acquireCurrentPosition, startPin, endPin]);

  const handleCalculate = useCallback(async () => {
    if (computing) return;
    setComputing(true);
    setErrorMsg(null);
    try {
      if (mode === 'stroke') {
        if (strokes.length === 0) return;
        const resp = await postFingerRoute({ strokes, region: activeRegion });
        setRoute({ kind: 'stroke', resp });
        if (resp.coordinates.length === 0) {
          setErrorMsg(
            "Couldn't snap your drawing to the trail network. Try drawing closer to known paths.",
          );
        }
      } else {
        if (!startPin || !endPin) return;
        // Stage 6m: waypoints in Tap-Reihenfolge — Backend gewichtet das
        // gegenüber dem Legacy-trailIds-Feld. Trail-Waypoints werden gewalkt,
        // Via-Waypoints sind Snap-Stops ohne Walk.
        const apiWaypoints = waypoints.map((w) =>
          w.kind === 'trail'
            ? { kind: 'trail' as const, trailId: w.trailId }
            : { kind: 'via' as const, point: w.lonLat },
        );
        // Wave 14em-fix: zoom-adaptiver Snap-Radius. Default war fix
        // 500 m → wer im Roadbike-Modus weit rausgezoomt einen Pin
        // drueckt, traf oft daneben → Backend bridged Luftlinie. Jetzt
        // skaliert der Radius exponentiell mit dem Zoom-Out:
        //   Zoom 15+ →  300 m  (Detail-Ansicht, Pin sitzt sehr genau)
        //   Zoom 13  →  600 m
        //   Zoom 11  → 1500 m
        //   Zoom  9  → 4500 m
        //   Zoom  7  → ~10 km  (Cap bei 12 km)
        // Faustformel: doppelter Radius pro Zoom-Stufe weniger ab 15.
        const currentZoom = mapRef.current?.getZoom() ?? 14;
        const snapRadiusM = Math.min(
          12000,
          Math.max(300, 300 * Math.pow(2, Math.max(0, 15 - currentZoom))),
        );
        // Debug-Log für User-Bug-Report ("via wird nicht berücksichtigt"):
        // im Safari-Console nach 'pin-route request' suchen — wenn das
        // waypoints-Array Vias enthält aber die Response sie ignoriert,
        // ist's ein Backend-Problem; wenn's gar keine Vias enthält, war
        // der Tap im Frontend nicht im waypoints-State.
        console.info('[navi] pin-route request', {
          start: startPin,
          end: endPin,
          waypointsCount: apiWaypoints.length,
          waypointsKinds: apiWaypoints.map((w) => w.kind),
          waypointsDetail: apiWaypoints,
          zoom: currentZoom.toFixed(1),
          snapRadiusM: Math.round(snapRadiusM),
        });
        const resp = await postPinRoute({
          start: startPin,
          end: endPin,
          waypoints: apiWaypoints,
          // trailIds bleibt als Backward-Compat-Hint mitgeschickt — falls
          // der Server-Build noch nicht waypoints kennt, fällt er auf
          // trailIds zurück.
          trailIds: selectedTrailIds,
          // Wave 14ca-fix21: Routing-Modus (mtb/road).
          mode: apiRouteMode,
          // Wave 14cf: Region steuert welche navi-{region}.sqlite genutzt wird.
          region: activeRegion,
          // Wave 14cv: Difficulty-Cap an den Router weitergeben. null = kein Filter.
          maxDifficulty,
          // Wave 14em-fix: dynamischer Pin-Snap-Radius (s. oben).
          pinSnapRadiusM: Math.round(snapRadiusM),
        });
        console.info('[navi] pin-route response', {
          lengthM: resp.lengthM,
          trailsUsed: resp.trailsUsed,
          warnings: resp.warnings,
          viasRouted: resp.debug.viasRouted,
          trailsLocked: resp.debug.trailsLocked,
        });
        setRoute({ kind: 'pin', resp });
        if (resp.coordinates.length === 0) {
          setErrorMsg(
            "Couldn't find a route between the pins. Try placing them closer to known paths.",
          );
        }
      }
    } catch (err) {
      if (err instanceof NaviRouteError) {
        if (err.status === 503) {
          setErrorMsg('Routing is temporarily unavailable. Try again in a moment.');
        } else if (err.status === 400) {
          setErrorMsg(`Can't route (${err.reason || 'bad_request'}).`);
        } else {
          setErrorMsg(`Routing failed (${err.status}).`);
        }
      } else {
        setErrorMsg('Network error while computing route.');
      }
    } finally {
      setComputing(false);
    }
  }, [
    mode,
    strokes,
    startPin,
    endPin,
    selectedTrailIds,
    computing,
    planMode,
    trails,
    apiRouteMode,
    activeRegion,
    maxDifficulty,
    waypoints,
  ]);

  // ULTRA Pit-Stop: sobald die vom Finder übergebenen Pins im State stehen,
  // Karte auf beide Pins fitten und die Route automatisch berechnen — der
  // User wollte ja "Route zum nächsten Stopp voraus", nicht noch manuell
  // tippen. Feuert genau einmal (pendingRef wird sofort zurückgesetzt).
  useEffect(() => {
    if (!pitstopPendingRef.current) return;
    if (!startPin || !endPin) return;
    pitstopPendingRef.current = false;
    const map = mapRef.current;
    if (map) {
      try {
        const bounds = new maplibregl.LngLatBounds(startPin, startPin);
        bounds.extend(endPin);
        map.fitBounds(bounds, { padding: 80, maxZoom: 14, duration: 800 });
      } catch (err) {
        console.warn('[create-custom] pit-stop fitBounds fehlgeschlagen', err);
      }
    }
    void handleCalculate();
  }, [startPin, endPin, handleCalculate]);

  const lengthKm = useMemo(
    () => (route ? (route.resp.lengthM / 1000).toFixed(1) : null),
    [route],
  );

  const trailsUsed = route?.resp.trailsUsed ?? [];
  const warnings = route?.resp.warnings ?? [];

  // Wave 14ca-fix64: Höhenprofil bereits waehrend der Planung.
  //
  // useTrailElevation braucht (trailId, GpxTrack). Wir bauen aus den
  // Route-Koordinaten einen synthetischen GpxTrack ohne ele — das zwingt
  // den Hook in den SRTM-Sampling-Pfad. Die trailId wird aus einem Hash
  // der ersten/letzten/laengsten coords abgeleitet, damit:
  //   a) jede neue Route einen frischen Cache-Eintrag bekommt (nicht den
  //      einer alten Route ueberschreibt)
  //   b) wiederholtes Auswaehlen derselben Route den Cache trifft, statt
  //      jedesmal SRTM neu zu sampeln (Auto-Approve nach Stop, Re-Render etc.)
  //
  // Das Profil rendert direkt unter dem Route-Info-Banner, damit der
  // User waehrend der Planung sofort die Anstiege sieht.
  const customRouteTrack = useMemo<GpxTrack | null>(() => {
    if (!route || route.resp.coordinates.length < 2) return null;
    return {
      name: 'custom-route-preview',
      points: route.resp.coordinates.map((c) => ({
        lat: c[1] as number,
        lon: c[0] as number,
        // ele bewusst weglassen — useTrailElevation laeuft sonst durch
        // hasElevationData() in den GPX-Direkt-Pfad und uebernimmt 0er
        // als Hoehen, was visuell sinnlos waere.
      })),
    };
  }, [route]);
  const customRouteId = useMemo<string | null>(() => {
    if (!customRouteTrack || customRouteTrack.points.length < 2) return null;
    const pts = customRouteTrack.points;
    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    // 6 Nachkommastellen ≈ 0.1m Genauigkeit. Bei selber Route → selbe id →
    // Cache-Hit; bei minimaler Verschiebung der Pins → neue id → frischer
    // SRTM-Sample-Run.
    const hash = `${first.lat.toFixed(5)},${first.lon.toFixed(5)}_${last.lat.toFixed(5)},${last.lon.toFixed(5)}_${pts.length}_${(route?.resp.lengthM ?? 0).toFixed(0)}`;
    return `custom-preview:${hash}`;
  }, [customRouteTrack, route]);
  const elevationData = useTrailElevation({
    trailId: customRouteId,
    track: customRouteTrack,
  });

  // Wave 14db: Route in Trail-Segmente zerlegen — exakt dasselbe Pattern
  // wie MapPage's CustomTour-Detail-View. Aus resp.routeSegments + coords
  // + trails bauen wir ElevationSegments + Unique-Trail-Liste, damit der
  // User VOR dem Save sieht welche Trails dabei sind und in welchen
  // Difficulty-Farben das Profil verläuft. User-Wunsch: "trails als blöcke
  // oben im höhenprofil zeigen, so dass ich schon vor dem SAVE überlegen
  // kann ob es so für mich ok ist".
  const previewElevationSegments = useMemo(() => {
    if (route?.kind !== 'pin') return [];
    const coords = route.resp.coordinates;
    const routeSegs = route.resp.routeSegments;
    if (!routeSegs || routeSegs.length === 0 || coords.length < 2) return [];
    return routeSegmentsToElevationSegments(
      routeSegs,
      coords,
      trails,
      // Wenn der User Trails explizit gepickt hat, dann ist
      // selectedTrailIds die "main trails"-Whitelist; ungewählte
      // Trail-Touches gelten als Connector (grau, kein Badge).
      // Wenn der User nichts gepickt hat (Auto-Route), zeigen wir alle
      // gewalkten Trails als Badges — also kein Whitelist.
      selectedTrailIds.length > 0 ? selectedTrailIds : null,
    );
  }, [route, trails, selectedTrailIds]);
  const previewUniqueTrails = useMemo(
    () => uniqueTrailsFromSegments(previewElevationSegments),
    [previewElevationSegments],
  );

  // Stage 6k: Warnings kategorisieren. Backend feuert Warnings sowohl für
  // echte Fehler (connector kann gar nix finden, trail unbekannt, walk
  // schlug fehl) ALS AUCH für harmlose Fallback-Events (soft-/plain-
  // Connector wurde benutzt statt strict — Route funktional identisch,
  // nur nicht ideal). Der bunte "Some parts couldn't be connected"-Banner
  // soll NUR bei echten Fehlern kommen, sonst verwirrt er.
  // Wave 14ei: start_equals_end ist nicht laenger ein "could not connect"-
  // Error sondern eine sinnvolle Mode-Bedingung (Loop). Wenn er trotzdem
  // kommt heisst er "User hat Loop gewaehlt aber keine Trails/Vias
  // ausgewaehlt" → dafuer rendern wir unten eine spezifische Message
  // statt der generischen "Some parts couldn't be connected".
  const REAL_ERROR_PATTERNS = [
    /^no_connector_/,
    /^no_path_/,
    /^walk_failed_/,
    /^trail_unknown_/,
    /^trail_empty_/,
    /^trail_coords_missing_/,
    /^start_not_snapped$/,
    /^end_not_snapped$/,
    /^invalid_pins$/,
  ];
  const realErrors = warnings.filter((w) =>
    REAL_ERROR_PATTERNS.some((re) => re.test(w)),
  );
  const isEmptyLoop = warnings.includes('start_equals_end');

  // Wave 14ej: Trails die der User gewaehlt hat aber die der Backend
  // NICHT walken konnte (kein Connector zum Trail-Entry, walk_failed, …).
  // Wir zeigen die in der Chip-Row mit Strikethrough + roten Border und
  // listen sie namentlich im Warning-Banner damit der User weiss, welche
  // Trails problematisch sind — statt eines kryptischen "3 not connected".
  const trailsUsedSet = useMemo(
    () => new Set(route?.resp.trailsUsed ?? []),
    [route],
  );
  const skippedTrailIds = useMemo(() => {
    if (!route) return [] as string[];
    return selectedTrailIds.filter((id) => !trailsUsedSet.has(id));
  }, [route, selectedTrailIds, trailsUsedSet]);

  const canCalculate = useMemo(() => {
    if (computing || !online) return false;
    if (mode === 'stroke') return strokes.length > 0;
    return !!startPin && !!endPin;
  }, [mode, strokes, startPin, endPin, computing, online]);

  const hasAnyContent =
    route !== null ||
    strokes.length > 0 ||
    startPin !== null ||
    endPin !== null ||
    waypoints.length > 0;

  /**
   * Wave 14ca-fix6: Pin-Step-Prompt teilt sich jetzt in zwei Sorten:
   *   - 'inline' für die kurzen Action-Prompts ("Tap the map to set start/
   *     destination") — die rendern als floating Hint oben über der Karte
   *     und stören nicht weil zu dem Zeitpunkt noch keine Start/End-Leiste
   *     angezeigt wird.
   *   - 'modal' für den langen Erklärtext (waypoints + via-pins) — der
   *     wird zentriert mit "Understood"-Button und einem 3-Strike-Dismiss
   *     in localStorage gerendert. Vorher war's ein Inline-Hint, der von
   *     der „Start set | End set"-Leiste verdeckt wurde (User-Bug-Report).
   */
  type PinPrompt =
    | { kind: 'inline'; text: string }
    | { kind: 'modal'; text: string }
    | null;
  const pinPrompt: PinPrompt = useMemo(() => {
    if (mode !== 'pins' || route) return null;
    // Wave 14eg-fix3: solange der PlanModePicker offen ist (planMode === null),
    // KEIN Prompt — der User hat noch nicht entschieden ob er manuell pinnt
    // oder von der GPS-Position startet. Vorher hing "Tap the map to set the
    // start point." über dem offenen Auswahl-Modal (User-Bug, ergibt null Sinn).
    if (planMode === null) return null;
    // 'from-here' / 'loop-here': Start = aktuelle GPS-Position, kein manueller
    // Tap noetig. Der Start-Punkt-Prompt erscheint nur bei pin-modes.
    if (planMode === 'pin-to-pin' && !startPin)
      return { kind: 'inline', text: 'Tap the map to set the start point.' };
    // Wave 14eh: loop-pin → 1 Tap setzt Start UND End, also brauchen wir
    // einen eigenen Text der das klar macht.
    if (planMode === 'loop-pin' && !startPin)
      return { kind: 'inline', text: 'Tap the map to set the loop start (also the end).' };
    if (!endPin) return { kind: 'inline', text: 'Tap the map to set the destination.' };
    if (waypoints.length === 0)
      return {
        kind: 'modal',
        text: 'Tap the map to add via-points along the way (in tap order). Tap a via again to remove.',
      };
    return null;
  }, [mode, planMode, startPin, endPin, waypoints.length, route]);

  // 3-Strike-Dismiss für den Modal-Prompt. Nach drei expliziten Understood-
  // Klicks (über mehrere Sessions kumuliert) erscheint er nie wieder; bis
  // dahin hat der User die Erklärung verinnerlicht. Sessions-lokales
  // `dismissedThisSession` versteckt den Modal sofort nach Klick, ohne
  // dass localStorage erst zwischen Renders synchronisiert werden muss.
  const TAP_TRAILS_PROMPT_KEY = 'adt_create_custom_tap_trails_seen';
  const TAP_TRAILS_PROMPT_LIMIT = 3;
  const [tapTrailsSeen, setTapTrailsSeen] = useState<number>(() => {
    try {
      const raw = window.localStorage.getItem(TAP_TRAILS_PROMPT_KEY);
      const n = raw == null ? 0 : Number.parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
      return 0;
    }
  });
  const [tapTrailsDismissedThisSession, setTapTrailsDismissedThisSession] =
    useState(false);
  const handleTapTrailsUnderstood = useCallback(() => {
    setTapTrailsSeen((prev) => {
      const next = prev + 1;
      try {
        window.localStorage.setItem(TAP_TRAILS_PROMPT_KEY, String(next));
      } catch {
        /* Privacy-Mode → bleibt nur in-Memory für diese Session */
      }
      return next;
    });
    setTapTrailsDismissedThisSession(true);
  }, []);
  const showTapTrailsModal =
    pinPrompt?.kind === 'modal' &&
    !tapTrailsDismissedThisSession &&
    tapTrailsSeen < TAP_TRAILS_PROMPT_LIMIT;

  // Wave 14eg-fix: Dismiss-Flag fuer den persistenten Inline-Hint. Per
  // Session-only — beim naechsten Plan-Start ist er wieder da, weil er
  // hilfreich genug ist um ihn beim Wiedereinstieg zu zeigen. Versteckt
  // sich automatisch sobald der User die erste Trail-/Via-Auswahl trifft
  // (waypoints.length > 0) — dann wird die Anleitung redundant.
  const [hintDismissedThisSession, setHintDismissedThisSession] =
    useState(false);
  const showInlineHint =
    mode === 'pins' &&
    !route &&
    !!startPin &&
    !!endPin &&
    !showTapTrailsModal &&
    waypoints.length === 0 &&
    !hintDismissedThisSession;

  // Lookup Trail-Name für Chip-Anzeige.
  const trailNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of trails) m.set(t.id, t.name || t.id);
    return m;
  }, [trails]);

  // Stage 6m-fix6: kompletter Trail-Lookup nach ID, für Difficulty-Color
  // auf den Post-Route-Badges + Highlight-Geometrie auf der Route-Polyline.
  const trailById = useMemo(() => {
    const m = new Map<string, CachedTrail>();
    for (const t of trails) m.set(t.id, t);
    return m;
  }, [trails]);

  // Hervorgehobener Trail (User klickt einen Badge auf der Post-Route-Card).
  // Wenn gesetzt, rendert der entsprechende Trail-Segment-Layer in einem
  // dickeren/leuchtenderen Stroke. Re-Klick auf denselben Badge schaltet
  // Highlight wieder aus.
  const [highlightedTrailId, setHighlightedTrailId] = useState<string | null>(
    null,
  );
  // Wenn die Route gewechselt wird (Recalculate, Clear), Highlight zurücksetzen.
  useEffect(() => {
    if (!route) setHighlightedTrailId(null);
  }, [route]);

  // Stage 6m-fix5: Highlight-Layer-Filter live aktualisieren wenn der
  // User einen Trail-Badge antippt. Der Layer rendert nur Features deren
  // `properties.trailId` exakt matcht — Sentinel `'__none__'` bedeutet
  // "keine Auswahl" (Layer unsichtbar).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const target = highlightedTrailId ?? '__none__';
    try {
      if (map.getLayer(ROUTE_HIGHLIGHT_LAYER)) {
        map.setFilter(ROUTE_HIGHLIGHT_LAYER, [
          '==',
          ['get', 'trailId'],
          target,
        ]);
      }
      if (map.getLayer(ROUTE_HIGHLIGHT_GLOW_LAYER)) {
        map.setFilter(ROUTE_HIGHLIGHT_GLOW_LAYER, [
          '==',
          ['get', 'trailId'],
          target,
        ]);
      }
    } catch {
      /* style noch nicht ready / Layer entfernt — egal */
    }
  }, [highlightedTrailId, mapReady, styleVersion]);

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  const handleZoom = useCallback((delta: number) => {
    const map = mapRef.current;
    if (!map) return;
    if (delta > 0) map.zoomIn();
    else map.zoomOut();
  }, []);

  // Stage 6j — Save-as-Custom-Trail.
  // Wave 14ch: kein window.prompt mehr — wir nehmen direkt einen sinnvollen
  // Default-Namen und der User kann ihn jederzeit in der Custom-Tours-Liste
  // umbenennen. Spart 1-2 Tap-Schritte vor dem Losfahren.
  // Wave 14dn: User-Wunsch zurückgedreht — JA wir wollen das Popup VOR
  // dem Save, weil der vorgeschlagene Name oft mies passt (erster Trail-
  // Name verwirrt wenn die Tour 4 Trails kombiniert). State + Modal unten.
  const [savingCustom, setSavingCustom] = useState(false);
  const [savedCustomId, setSavedCustomId] = useState<string | null>(null);
  const [saveNamePrompt, setSaveNamePrompt] = useState<{
    defaultName: string;
    autoRide: boolean;
  } | null>(null);

  /** Berechnet den Default-Namen für die aktuelle Route. Wird sowohl beim
   *  Öffnen des Name-Prompts als auch direkt im Save genutzt. */
  const computeDefaultTrailName = useCallback((): string => {
    if (!route) return 'Custom route';
    const fromSelected =
      selectedTrailIds.length > 0 &&
      trails.find((t) => t.id === selectedTrailIds[0])?.name;
    const fromUsed = route.resp.trailsUsed[0];
    const base =
      fromSelected || fromUsed || `Custom route ${new Date().toLocaleDateString()}`;
    return String(base).trim() || 'Custom route';
  }, [route, selectedTrailIds, trails]);

  /** Öffnet den Name-Editor-Modal — beide Save-Buttons gehen jetzt hier
   *  durch, damit der User vor dem Persistieren den Namen anpassen kann. */
  const openSavePrompt = useCallback(
    (opts?: { autoRide?: boolean }) => {
      if (!route || route.resp.coordinates.length < 2 || savingCustom) return;
      setSaveNamePrompt({
        defaultName: computeDefaultTrailName(),
        autoRide: !!opts?.autoRide,
      });
    },
    [route, savingCustom, computeDefaultTrailName],
  );

  const handleSaveAsCustomTrail = useCallback(async (opts?: {
    autoRide?: boolean;
    /** Wave 14dn: optionaler explizit gewählter Name aus dem Save-Modal.
     *  Wenn null/leer → fällt auf computed default zurück. */
    name?: string;
  }) => {
    if (!route || route.resp.coordinates.length < 2 || savingCustom) return;
    const trimmed =
      (opts?.name && opts.name.trim()) ||
      computeDefaultTrailName();
    setSavingCustom(true);
    try {
      // Wave 14cf-fix15: Default-Difficulty = härtester der einbezogenen Trails.
      // DIFFICULTY_ORDER ist sortiert von "leicht" (uphill, easy) zu "schwer"
      // (extreme). Wir picken den höchsten Index unter den verwendeten Trails;
      // Trails mit unbekannter Difficulty werden ignoriert (sonst würde "unknown"
      // den Pick verfälschen). Der User kann das im Detail-View jederzeit
      // ändern (EditCustomTrailModal).
      const usedIds = route.kind === 'pin' ? selectedTrailIds : [];
      const usedDifficulties = usedIds
        .map((id) => trails.find((t) => t.id === id)?.difficulty)
        .filter((d): d is Difficulty => !!d && d !== 'unknown');
      let computedDifficulty = 'unknown';
      if (usedDifficulties.length > 0) {
        let bestIdx = -1;
        for (const d of usedDifficulties) {
          const idx = DIFFICULTY_ORDER.indexOf(d as Difficulty);
          if (idx > bestIdx) bestIdx = idx;
        }
        if (bestIdx >= 0) {
          computedDifficulty = DIFFICULTY_ORDER[bestIdx]!;
        }
      }

      const saved = await saveCustomTrail({
        name: trimmed,
        coords: route.resp.coordinates,
        lengthM: route.resp.lengthM,
        trailsUsed: route.resp.trailsUsed,
        trailIds: route.kind === 'pin' ? selectedTrailIds : undefined,
        startPin: route.kind === 'pin' ? startPin ?? undefined : undefined,
        endPin: route.kind === 'pin' ? endPin ?? undefined : undefined,
        difficulty: computedDifficulty,
        // Wave 14ca-fix64: pro-Segment-Trail-Mapping persistieren, damit
        // beim Oeffnen das Hoehenprofil nach Difficulty einfaerbbar ist
        // und die Trail-Liste mit Click-Highlight gebaut werden kann.
        // Pin-Routes liefern routeSegments im response; Stroke-Routes
        // (FingerRoute) noch nicht — dort bleibt das Feld leer.
        routeSegments:
          route.kind === 'pin' && route.resp.routeSegments
            ? route.resp.routeSegments
            : undefined,
      });
      setSavedCustomId(saved.id);
      // Wave 14ch: "Save & ride now"-Pfad. Publish ein Map-Intent das die
      // MapPage anweist, den frisch gespeicherten Custom-Trail zu öffnen +
      // sofort den Ride-Modus zu starten. Dann zur Map navigieren. Der
      // Ride-Intro-Modal greift trotzdem beim ersten Mal — der gilt für
      // jeden Ride-Start, nicht nur RIDE-Button-Taps.
      if (opts?.autoRide) {
        mapIntentsBus.publish({
          customTrailId: saved.id,
          resetMode: false,
          autoStartRide: true,
        });
        navigate('/');
      }
    } catch (err) {
      console.error('[createCustom] save custom trail failed', err);
      setErrorMsg('Could not save custom trail — try again.');
    } finally {
      setSavingCustom(false);
    }
  }, [route, selectedTrailIds, trails, startPin, endPin, savingCustom, navigate]);

  // ULTRA: „RIDE" aus dem Pit-Stop-Voraus-Flow — sofort losfahren OHNE die
  // Route als Custom-Tour zu speichern. Wir bauen eine flüchtige Trail-Hülle
  // aus den Routen-Koordinaten und schicken sie als Map-Intent (autoStartRide).
  const handleRideWithoutSave = useCallback(() => {
    if (!route || route.resp.coordinates.length < 2) return;
    const ephemeralTrail = ephemeralRouteAsTrail({
      name: computeDefaultTrailName(),
      coords: route.resp.coordinates,
    });
    mapIntentsBus.publish({
      ephemeralTrail,
      resetMode: false,
      autoStartRide: true,
    });
    navigate('/');
  }, [route, computeDefaultTrailName, navigate]);

  return (
    <div className="absolute inset-0 bg-turbo-900">
      <div ref={containerRef} className="absolute inset-0" />

      {/* Wave 14dy: Einmalige Touch-Intro fuer Handy-User — erklaert das
          1-Finger vs 2-Finger-Verhalten BEVOR der User irritiert ist
          warum die Karte sich nicht ziehen laesst. Auf Desktop wird das
          Modal nie gerendert (matchMedia checks im State-Init). Dismiss
          merkt sich's in localStorage, kommt also nur einmal. */}
      {showTouchIntro && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/55 backdrop-blur-[1px]"
          onClick={dismissTouchIntro}
          role="dialog"
          aria-modal="true"
          aria-labelledby="touch-intro-title"
        >
          <div
            className="bg-turbo-800 border border-turbo-600/40 rounded-2xl p-5 max-w-sm w-full shadow-2xl text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="touch-intro-title"
              className="text-lg font-semibold mb-3"
            >
              How to plan your tour
            </h3>
            <div className="space-y-3 text-sm text-white/90 leading-snug">
              <div className="flex items-start gap-3">
                <span className="text-2xl leading-none">👆</span>
                <div>
                  <div className="font-medium">1 finger</div>
                  <div className="text-white/70 text-[13px]">
                    Tap to set start &amp; end pins, and to pick trails
                    along the way.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl leading-none">✌️</span>
                <div>
                  <div className="font-medium">2 fingers</div>
                  <div className="text-white/70 text-[13px]">
                    Drag with two fingers to move the map. Pinch to
                    zoom in and out.
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissTouchIntro}
              className="mt-5 w-full bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white font-semibold py-2.5 rounded-lg transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}


      {/* Wave 14cn: Akku-Wechsel-Stationen auch im Create-Custom-Flow
          sichtbar. Tap auf eine Pille setzt die Station als End-Pin
          (statt das Detail-Sheet zu öffnen wie auf der Hauptkarte).
          Wenn schon ein Start-Pin gesetzt ist, fragen wir vorher kurz —
          sonst überschreiben wir den End-Pin direkt. POI-Toggle und
          VL-Gate steuert das Component selbst. */}
      <BatteryStationOverlay
        // Wave 14cw: gesnappte Pin-Coords mitgeben → die Station bekommt
        // einen Sky-Glow-Rahmen damit der User merkt "ja, die ist mein Ziel".
        highlightedAt={[startPin, endPin]}
        onStationTap={(st) => {
          setEndPin([st.lon, st.lat]);
        }}
      />

      {/* Top-Bar */}
      <div className="absolute top-0 left-0 right-0 z-10 safe-top px-3 pt-2 flex items-center gap-2">
        <Link
          to="/navi"
          className="inline-flex items-center gap-1 rounded-full bg-turbo-900/70 backdrop-blur-sm border border-sky-500/30 text-sky-300 text-sm px-3 py-1.5 no-underline"
        >
          <span aria-hidden>←</span> NAVI
        </Link>
        {/* ULTRA: Pins/Stroke- + MTB-Toggle + Difficulty-Cap entfernt.
            Stattdessen: Kürzeste / Flachste (Steigungs-Minimierung). */}
        <button
          type="button"
          onClick={() => {
            setFlattest(!flattest);
            setRoute(null); // alter Plan invalid mit neuem Modus
          }}
          className={`inline-flex items-center gap-1 rounded-full text-[11px] px-2 py-1 border transition-colors ${
            flattest
              ? 'bg-teal-500/25 border-teal-400/60 text-teal-100 active:bg-teal-500/40'
              : 'bg-sky-500/15 border-sky-400/40 text-sky-200 active:bg-sky-500/30'
          }`}
          title={
            flattest
              ? 'Flattest route (minimises climbing). Tap for shortest.'
              : 'Shortest route. Tap for flattest (least climbing).'
          }
          aria-pressed={flattest}
        >
          {flattest ? '🏔️ Flachste' : '🛣 Kürzeste'}
        </button>
        {/* Stage 6m — Plan-Mode-Badge + "Change plan"-Reopener. Nur im
            pins-Mode sinnvoll (stroke-Mode hat keine Pins). */}
        {mode === 'pins' && planMode && (
          <button
            type="button"
            onClick={() => setPlanMode(null)}
            className="inline-flex items-center gap-1 rounded-full bg-sky-500/20 border border-sky-400/40 text-sky-200 text-[11px] px-2 py-1 active:bg-sky-500/35"
            title="Change plan mode"
          >
            {planMode === 'from-here' && '📍 From here'}
            {planMode === 'pin-to-pin' && '📌 Pin to pin'}
            {planMode === 'loop-here' && '🔁 Loop from here'}
            {planMode === 'loop-pin' && '🔁 Loop from a pin'}
          </button>
        )}
        {/* POI-Toggle ist jetzt in der unten platzierten BottomBar (siehe
            unten), damit's an derselben gewohnten Stelle wie auf MapPage
            ist (User-Wunsch 14ce-fix3). */}
        {!online && (
          <span className="ml-auto rounded-full bg-yellow-500/15 border border-yellow-400/40 text-yellow-300 text-xs px-2 py-1">
            Offline
          </span>
        )}
      </div>

      {/* Zoom-Buttons + My-Position rechts mittig — da dragPan deaktiviert
          ist und Pinch nicht auf Desktop + für Einhand-Bedienung auf dem
          Handy oft schwierig, sind +/- buttons essentiell. Analog zu
          MapPage's ZoomButtons, nur hier oben rechts statt unten rechts
          (unten sitzt die Action-Bar).
          Stage 6m: zusätzlich ein Locate-Button (📍) — holt per one-shot
          GPS-Fix die aktuelle Position und setzt sie auf den nächsten
          leeren Pin (oder Start bei Loop). */}
      <div
        className="absolute right-3 z-10 flex flex-col gap-3 pointer-events-none"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4rem)' }}
        aria-hidden={false}
      >
        <button
          type="button"
          onClick={() => handleZoom(+1)}
          className="pointer-events-auto w-12 h-12 rounded-full bg-turbo-900/65 border border-white/25 backdrop-blur-md shadow-lg flex items-center justify-center text-white text-2xl font-light leading-none active:bg-turbo-900/85"
          title="Zoom in"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => handleZoom(-1)}
          className="pointer-events-auto w-12 h-12 rounded-full bg-turbo-900/65 border border-white/25 backdrop-blur-md shadow-lg flex items-center justify-center text-white text-2xl font-light leading-none active:bg-turbo-900/85"
          title="Zoom out"
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          onClick={handleUseMyPosition}
          disabled={gpsBusy}
          className="pointer-events-auto w-12 h-12 rounded-full bg-sky-500/25 border border-sky-400/50 backdrop-blur-md shadow-lg flex items-center justify-center text-sky-100 text-xl leading-none active:bg-sky-500/40 disabled:opacity-50"
          title="Use my current location"
          aria-label="Use my current location"
        >
          {gpsBusy ? '…' : '📍'}
        </button>
      </div>

      {/* GPS-Fehler als kurze Toast-Pille (3s sichtbar, User kann schließen). */}
      {gpsError && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-20 px-3 pointer-events-auto"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' }}
        >
          <button
            type="button"
            onClick={() => setGpsError(null)}
            className="rounded-full bg-rose-500/25 border border-rose-400/40 text-rose-100 text-xs px-3 py-1.5"
          >
            {gpsError} · tap to dismiss
          </button>
        </div>
      )}

      {/* Stage 6m — Planungs-Modus-Picker (initial + via "Change plan").
          Wave 14ca-fix22: routeMode (mtb/road) wird im Modal direkt
          gesetzt — der User entscheidet vor der Scenario-Wahl ob er
          eine Trail-Tour oder eine Asphalt-Heimfahrt plant. */}
      {/* Wave 14dn: Save-Name-Editor-Popup. Erscheint VOR dem eigentlichen
          persist. User kann den Default editieren, Cancel verlässt ohne
          speichern, Save persistiert mit dem (möglicherweise) editierten Namen.
          Für Save&Ride-now wird autoRide aus dem prompt-State durchgereicht. */}
      {saveNamePrompt && (
        <SaveNameModal
          defaultName={saveNamePrompt.defaultName}
          autoRide={saveNamePrompt.autoRide}
          busy={savingCustom}
          onCancel={() => setSaveNamePrompt(null)}
          onConfirm={async (name) => {
            const autoRide = saveNamePrompt.autoRide;
            setSaveNamePrompt(null);
            await handleSaveAsCustomTrail({ name, autoRide });
          }}
        />
      )}

      {planMode === null && (
        <PlanModePicker
          onPick={handlePickPlanMode}
          onClose={() => setPlanMode('pin-to-pin')}
          routeMode={routeMode}
          onRouteModeChange={(next) => {
            setRouteMode(next);
            if (next) setSelectedTrailIds([]);
            setRoute(null);
          }}
          // Wave 14cx: Max-Difficulty als bewusste Frage im Picker.
          // User-Wunsch: "im zuge dessen NICHT gefragt wie schwer maximal
          // die trails sein sollen, schlecht! sollte kommen die frage!"
          maxDifficultyRaw={maxDifficultyRaw}
          onMaxDifficultyChange={(next) => {
            setMaxDifficultyRaw(next);
            setRoute(null);
          }}
        />
      )}

      {/* Pin-Step-Prompt — inline für die kurzen Action-Prompts (Tap to set
          start/destination). Sitzt oben mittig, kein Backdrop.
          Wave 14eb: Position auf safe-area + 4.5rem damit der Prompt unter
          die Top-Toolbar (NAVI / Mode-Switch / Style-Switcher) ruckt statt
          sie zu verdecken. Vorher top-16 → kollidierte mit der Toolbar bei
          iPhones mit Notch. */}
      {pinPrompt?.kind === 'inline' && (
        // Wave 14eg-fix2: z-30 (vorher z-10) — die Pins-Status-Chips (z-10,
        // weiter unten im DOM) painteten sonst über den Prompt und verdeckten
        // ihn (User-Bug). z-30 hebt ihn klar darüber. Font text-base (vorher
        // text-sm) für bessere Lesbarkeit am Phone, opaker BG + shadow-xl +
        // Caret nach unten machen die Bubble eindeutig als Map-Anweisung
        // lesbar statt mit dem Header zu verschmelzen.
        <div
          className="absolute inset-x-0 z-30 px-4 flex flex-col items-center pointer-events-none"
          // Wave 14eg-fix5: Position abhängig davon ob die Status-Chips
          // gerendert sind. Die Chips (top-28 = 7rem) erscheinen sobald ein
          // startPin gesetzt ist — der Ziel-Prompt sitzt knapp darunter bei
          // 8rem (~0.5-1rem Luft unter der Chip-Unterkante). 11rem (fix3) war
          // überkorrigiert → sah aus wie ein Render-Bug statt bewusster
          // Abstand. Ohne Chips (Start-Punkt-Prompt) bleibt er hoch unter der
          // Toolbar (4.5rem) — da war keine Beschwerde.
          style={{
            top: startPin
              ? 'calc(env(safe-area-inset-top, 0px) + 8rem)'
              : 'calc(env(safe-area-inset-top, 0px) + 4.5rem)',
          }}
        >
          <div className="rounded-xl bg-turbo-900/95 backdrop-blur-sm border border-sky-500/40 text-white text-base font-medium px-4 py-2.5 max-w-xs shadow-xl">
            {pinPrompt.text}
          </div>
          {/* Caret nach unten — signalisiert "tipp auf die Karte darunter". */}
          <div
            className="w-3 h-3 -mt-1.5 rotate-45 bg-turbo-900/95 border-r border-b border-sky-500/40"
            aria-hidden="true"
          />
        </div>
      )}

      {/* Wave 14eg: Tap-Trails-Modal als ausfuehrlicher 3-Punkte-Guide.
          Wird nach 3 expliziten Understood-Klicks nie wieder angezeigt
          (localStorage). Backdrop blockt Map-Taps bis bestaetigt. */}
      {showTapTrailsModal && pinPrompt?.kind === 'modal' && (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center px-6 bg-black/55 backdrop-blur-[1px]"
        >
          <div
            className="rounded-2xl shadow-2xl p-5 max-w-sm w-full text-white border border-sky-500/40 bg-turbo-800"
          >
            <h3 className="text-base font-semibold mb-3">
              Start &amp; destination are set — now build the route
            </h3>
            <div className="space-y-3 text-sm leading-snug">
              <div className="flex items-start gap-3">
                <span className="text-lg leading-none mt-0.5">🛤️</span>
                <div>
                  <div className="font-medium">Tap trails</div>
                  <div className="text-white/70 text-[13px]">
                    Tap any visible trail line to include it in your route.
                    Tap again to remove. The order you tap = the order you'll ride.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-lg leading-none mt-0.5">📍</span>
                <div>
                  <div className="font-medium">Tap empty map for a via-point</div>
                  <div className="text-white/70 text-[13px]">
                    Drops a waypoint the route has to pass through —
                    useful when no road goes exactly where you want.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-lg leading-none mt-0.5">↔️</span>
                <div>
                  <div className="font-medium">Reorder at the top</div>
                  <div className="text-white/70 text-[13px]">
                    The chip-row above the map shows your trails &amp; via-points
                    in tap order. Use the small arrows on each chip to move it
                    left or right, or × to remove.
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleTapTrailsUnderstood}
              className="mt-5 w-full bg-sky-500 hover:bg-sky-400 active:bg-sky-600 text-white font-semibold rounded-lg py-2.5 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Wave 14eg-fix4: Via-Point-Hint OBEN am Karten-Header, gleiches
          Bubble-Pattern + Position wie die pinPrompt-Tooltips ("Tap to set
          start/destination"). Grund (User-Wunsch): nachdem der User die
          ersten zwei Tooltips OBEN gesehen hat, springt der Blick beim
          dritten Schritt automatisch nach oben — ein Hinweis unten wird
          übersehen. Position 8rem = knapp unter den Start/End-Status-Chips
          (die sind da, weil beide Pins gesetzt sind), identisch zum Ziel-
          Prompt. Verschwindet automatisch sobald waypoints > 0 (Trail/Via
          gepickt) oder per × manuell. */}
      {showInlineHint && (
        <div
          className="absolute inset-x-0 z-30 px-4 flex flex-col items-center pointer-events-none"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 8rem)' }}
        >
          <div className="relative rounded-xl bg-turbo-900/95 backdrop-blur-sm border border-sky-500/40 text-white text-base font-medium px-4 py-2.5 max-w-xs shadow-xl pointer-events-auto">
            Tap trails or empty map for a via-point — reorder with the arrows above.
            <button
              type="button"
              onClick={() => setHintDismissedThisSession(true)}
              aria-label="Dismiss hint"
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-turbo-900 border border-sky-500/40 flex items-center justify-center text-white/70 hover:text-white text-sm leading-none shadow"
            >
              ×
            </button>
          </div>
          {/* Caret nach unten — gleiche Optik wie die pinPrompt-Bubbles. */}
          <div
            className="w-3 h-3 -mt-1.5 rotate-45 bg-turbo-900/95 border-r border-b border-sky-500/40"
            aria-hidden="true"
          />
        </div>
      )}


      {/* Tap-miss Flash (Wave 14eb: safe-area + 4.5rem wie Pin-Prompt) */}
      {tapMissedAt != null && (
        <div
          key={tapMissedAt}
          className="absolute inset-x-0 z-20 px-4 text-center pointer-events-none animate-pulse"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
        >
          <span className="inline-block rounded-full bg-yellow-500/20 border border-yellow-400/40 text-yellow-200 text-xs px-3 py-1">
            Nothing to select here — try zooming in.
          </span>
        </div>
      )}

      {/* Stroke-Mode Hint (Wave 14eb: safe-area + 4.5rem statt top-16) */}
      {mode === 'stroke' && !hasAnyContent && (
        <div
          className="absolute inset-x-0 z-10 px-6 text-center pointer-events-none"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
        >
          <div className="inline-block rounded-xl bg-turbo-900/80 backdrop-blur-sm border border-sky-500/25 text-white/85 text-sm px-4 py-3 max-w-xs space-y-1">
            <div>
              Draw a rough tour with one finger. Two fingers pan &amp; zoom.
            </div>
            {/* Wave 14ea/ed: Desktop-Hinweis — auf Desktop ist 1-Finger-Pan
                jetzt nativ ueber MapLibre, deswegen muss man fuer's
                Zeichnen T halten (Alt/Shift kollidieren mit MapLibre's
                eigenen Gestures auf Mac). Nur sichtbar wenn coarse-
                Pointer NICHT zutrifft (also Maus/Pen). */}
            {!isCoarsePointerRef.current && (
              <div className="text-white/60 text-[12px]">
                On desktop: hold <kbd className="px-1 py-0.5 rounded bg-white/10 text-white/85">T</kbd> while dragging to draw the tour — without it the mouse pans the map.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pins-Mode: Status + Trail-Auswahl-Chips */}
      {mode === 'pins' && (startPin || endPin || waypoints.length > 0) && !route && (
        <div className="absolute left-3 right-3 top-28 z-10 rounded-xl bg-turbo-900/80 backdrop-blur-sm border border-sky-500/25 px-3 py-2 text-xs text-white/90 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" />
              {startPin ? 'Start set' : 'Start —'}
              {startPin && (
                <button
                  type="button"
                  onClick={handleResetStart}
                  className="ml-1 text-sky-300 underline decoration-sky-300/50 underline-offset-2"
                >
                  change
                </button>
              )}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-400 inline-block" />
              {endPin ? 'End set' : 'End —'}
              {endPin && (
                <button
                  type="button"
                  onClick={handleResetEnd}
                  className="ml-1 text-sky-300 underline decoration-sky-300/50 underline-offset-2"
                >
                  change
                </button>
              )}
            </span>
          </div>
          {waypoints.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1 border-t border-sky-500/20">
              {/* Wave 14ca-fix64: Reorder-Pfeile pro Pill.
                  User-Wunsch: "ich kann erst die 2 Trails adden, dann via —
                  aber dann landet via ans Ende. Will das verschieben können."
                  Komoot löst das per Drag-and-Drop, wir gehen den glove-
                  freundlicheren Weg: explizite ←/→-Buttons pro Pill, die
                  mit dem Nachbarn swappen. Pill-Click selbst macht jetzt
                  nichts mehr — nur die einzelnen Buttons reagieren (Pfeile
                  und ×). Vorher loeste der ganze Pill-Klick ein Remove
                  aus, was bei dem neuen Layout (mehrere Sub-Buttons) zu
                  Konflikten gefuehrt haette und ohnehin gefaehrlich war
                  (versehentliches Loeschen). */}
              {waypoints.map((wp, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === waypoints.length - 1;
                const canReorder = waypoints.length > 1;
                const moveLeft = () => {
                  if (isFirst) return;
                  setWaypoints((prev) => {
                    const next = prev.slice();
                    [next[idx - 1], next[idx]] = [next[idx]!, next[idx - 1]!];
                    return next;
                  });
                };
                const moveRight = () => {
                  if (isLast) return;
                  setWaypoints((prev) => {
                    const next = prev.slice();
                    [next[idx], next[idx + 1]] = [next[idx + 1]!, next[idx]!];
                    return next;
                  });
                };
                const onRemove = () => {
                  if (wp.kind === 'trail') {
                    handleRemoveTrail(wp.trailId);
                  } else {
                    setWaypoints((prev) =>
                      prev.filter(
                        (w) => !(w.kind === 'via' && w.id === wp.id),
                      ),
                    );
                  }
                };
                // Wave 14ej: Trail wurde vom Router geskipped → rote
                // Farb-Variante + Strikethrough, damit der User sofort
                // sieht WELCHE Trail-Chips nicht in die Route geflossen
                // sind.
                const isSkipped =
                  wp.kind === 'trail' && trailsUsedSet.size > 0 &&
                  !trailsUsedSet.has(wp.trailId);
                // Farben pro kind beibehalten, aber Layout vereinheitlicht.
                const colorClasses =
                  wp.kind === 'trail'
                    ? isSkipped
                      ? {
                          pill:
                            'bg-red-500/20 border-red-400/60 text-red-100 line-through decoration-red-300/70 decoration-1',
                          chip: 'bg-red-400/30 text-red-50 no-underline',
                          arrow: 'text-red-100/85 hover:text-red-50',
                          remove: 'text-red-100/70 hover:text-red-50',
                        }
                      : {
                        pill:
                          'bg-amber-500/20 border-amber-400/50 text-amber-100',
                        chip: 'bg-amber-400/30 text-amber-50',
                        arrow: 'text-amber-100/85 hover:text-amber-50',
                        remove: 'text-amber-100/70 hover:text-amber-50',
                      }
                    : {
                        pill:
                          'bg-yellow-500/20 border-yellow-400/50 text-yellow-100',
                        chip: 'bg-yellow-400/30 text-yellow-50',
                        arrow: 'text-yellow-100/85 hover:text-yellow-50',
                        remove: 'text-yellow-100/70 hover:text-yellow-50',
                      };
                const label =
                  wp.kind === 'trail'
                    ? trailNameById.get(wp.trailId) ||
                      displayNameForTrailId(wp.trailId)
                    : 'via';
                const titleAttr =
                  wp.kind === 'via'
                    ? `via ${wp.lonLat[1].toFixed(4)}, ${wp.lonLat[0].toFixed(4)}`
                    : isSkipped
                      ? `${label} — skipped by the router (couldn't reach it from the others)`
                      : undefined;
                const key =
                  wp.kind === 'trail' ? `t-${wp.trailId}` : `v-${wp.id}`;
                return (
                  <div
                    key={key}
                    className={`inline-flex items-center gap-0.5 rounded-full border text-[11px] pl-1.5 pr-1 py-0.5 ${colorClasses.pill}`}
                    title={titleAttr}
                  >
                    <span
                      className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold mr-1 ${colorClasses.chip}`}
                    >
                      {idx + 1}
                    </span>
                    <span className="mr-1">{label}</span>
                    {canReorder && (
                      <>
                        <button
                          type="button"
                          onClick={moveLeft}
                          disabled={isFirst}
                          aria-label="Move earlier in route"
                          title="Move earlier"
                          className={`w-6 h-6 inline-flex items-center justify-center rounded-full transition-colors ${
                            isFirst
                              ? 'opacity-30 cursor-not-allowed'
                              : `${colorClasses.arrow} hover:bg-white/10 active:bg-white/20`
                          }`}
                        >
                          ←
                        </button>
                        <button
                          type="button"
                          onClick={moveRight}
                          disabled={isLast}
                          aria-label="Move later in route"
                          title="Move later"
                          className={`w-6 h-6 inline-flex items-center justify-center rounded-full transition-colors ${
                            isLast
                              ? 'opacity-30 cursor-not-allowed'
                              : `${colorClasses.arrow} hover:bg-white/10 active:bg-white/20`
                          }`}
                        >
                          →
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={onRemove}
                      aria-label={
                        wp.kind === 'trail'
                          ? `Remove ${label} from route`
                          : 'Remove via point from route'
                      }
                      title="Remove"
                      className={`w-6 h-6 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/10 active:bg-white/20 ${colorClasses.remove}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Route-Info-Banner */}
      {route && route.resp.coordinates.length > 0 && (
        <div className="absolute left-3 right-3 top-14 z-10 rounded-xl bg-turbo-900/80 backdrop-blur-sm border border-sky-500/40 p-3 text-white shadow-lg">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-sky-300 tabular-nums">
              {lengthKm} <span className="text-sm font-normal text-white/70">km</span>
            </span>
            <span className="text-xs text-white/50 ml-auto">
              {route.resp.computeMs} ms
              {route.kind === 'pin' &&
                (route.resp.debug.trailsLocked?.length ?? 0) > 0 &&
                ` · ${route.resp.debug.trailsLocked!.length} locked`}
              {route.kind === 'pin' &&
                (route.resp.debug.viasRouted ?? 0) > 0 &&
                ` · ${route.resp.debug.viasRouted} via`}
            </span>
          </div>
          {trailsUsed.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {/* Stage 6m-fix6: Badges sind jetzt klickbar + tragen die
                  Difficulty-Farbe des Trails als kleinen Swatch links.
                  Klick highlightet das entsprechende Trail-Segment auf der
                  Route-Polyline (dickerer/heller Stroke). Erneuter Klick =
                  Highlight aus. */}
              {trailsUsed.map((t) => {
                const trail = trailById.get(t);
                const swatchColor =
                  trail?.colorHex ||
                  (trail ? DIFFICULTY_COLOR[trail.difficulty] : '#fb923c') ||
                  '#fb923c';
                const isHighlighted = highlightedTrailId === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() =>
                      setHighlightedTrailId((prev) =>
                        prev === t ? null : t,
                      )
                    }
                    aria-pressed={isHighlighted}
                    className={`inline-flex items-center gap-1.5 rounded-full text-[11px] px-2 py-0.5 border transition-colors ${
                      isHighlighted
                        ? 'bg-white/20 border-white/60 text-white shadow-sm'
                        : 'bg-orange-500/15 border-orange-400/30 text-orange-200 hover:bg-orange-500/25 active:bg-orange-500/30'
                    }`}
                  >
                    {/* Difficulty-Color-Swatch — kleiner Kreis links vom Namen,
                        damit der User auf einen Blick sieht ob's ein grüner
                        oder schwarzer Trail ist. White-Border damit Schwarz/
                        Stripe-Patterns auf dem dunklen Sheet sichtbar bleiben. */}
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0 border border-white/50"
                      style={{ background: swatchColor }}
                      aria-hidden="true"
                    />
                    <span>{trailNameById.get(t) || displayNameForTrailId(t)}</span>
                  </button>
                );
              })}
            </div>
          )}
          {realErrors.length > 0 && skippedTrailIds.length === 0 && (
            <div className="mt-2 text-[11px] text-yellow-300/90">
              Some parts couldn't be connected ({realErrors.length}).
            </div>
          )}
          {isEmptyLoop && (
            // Wave 14ei: Loop ohne Waypoints → keine Tour zum Fahren.
            // Statt der missverstaendlichen "couldn't connect"-Meldung
            // sagen wir's klar: Pick mindestens einen Trail oder Via.
            <div className="mt-2 text-[11px] text-yellow-300/90">
              A loop needs at least one trail or via-point in between —
              tap a trail line or an empty spot on the map.
            </div>
          )}
          {/* Wave 14ej: Skipped-Trails Klartext-Liste. Statt "3 not
              connected" sagen wir genau welche Trails der Router nicht
              walken konnte. So weiss der User, ob er die Auswahl
              anpassen will (Trail entfernen / Via einfuegen damit der
              Router den Trail erreicht). */}
          {skippedTrailIds.length > 0 && (
            <div className="mt-2 text-[11px] text-yellow-300/90 leading-snug">
              <div className="font-semibold">
                {skippedTrailIds.length}{' '}
                {skippedTrailIds.length === 1 ? 'trail was' : 'trails were'}{' '}
                skipped — the router couldn't reach{' '}
                {skippedTrailIds.length === 1 ? 'it' : 'them'} from your other
                picks:
              </div>
              <div className="mt-1 text-white/80">
                {skippedTrailIds
                  .map(
                    (id) =>
                      trailNameById.get(id) || displayNameForTrailId(id),
                  )
                  .join(', ')}
              </div>
              <div className="mt-1 text-white/55 text-[10px]">
                Tip: remove the skipped trails, or add a via-point closer
                to {skippedTrailIds.length === 1 ? 'it' : 'them'} so the
                router has a hint.
              </div>
            </div>
          )}

          {/* Wave 14ca-fix64: Höhenprofil schon waehrend Planung.
              elevationData kommt aus useTrailElevation mit synthetischer
              trailId. Solange source='pending' zeigen wir einen kleinen
              Lade-Hint statt eines leeren SVG; bei source='none' (kein
              Cache + Sampling failed) blenden wir es ganz aus damit kein
              kaputtes Widget rumliegt. */}
          {elevationData.source === 'pending' && (
            <div className="mt-3 text-[11px] text-white/55 italic">
              Höhenprofil wird berechnet…
            </div>
          )}
          {elevationData.elevations &&
            elevationData.elevations.length > 1 &&
            elevationData.source !== 'pending' && (
              <div className="mt-3 -mx-1">
                {/* Wave 14db: Trail-Badges OBERHALB des Profils (matched
                    den Save-Trail-Detail-View). Jeder Badge zeigt
                    Difficulty-Color-Swatch + Trailname + Länge. So sieht
                    der User vor dem SAVE was die Route überfährt. */}
                {previewUniqueTrails.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5 px-1">
                    <span className="text-[11px] text-white/55 self-center mr-0.5">
                      Trails:
                    </span>
                    {previewUniqueTrails.map((t) => {
                      const lengthLabel =
                        t.totalMeters >= 1000
                          ? `${(t.totalMeters / 1000).toFixed(1)} km`
                          : `${Math.round(t.totalMeters)} m`;
                      return (
                        <span
                          key={t.trailId}
                          className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] border bg-turbo-700/40 border-turbo-700/60 text-white/85"
                          title={`${t.trailName} — ${lengthLabel}`}
                        >
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full border border-white/35 shrink-0"
                            style={{ background: t.color }}
                            aria-hidden="true"
                          />
                          <span className="truncate max-w-[10rem]">
                            {t.trailName}
                          </span>
                          <span className="text-white/45 tabular-nums">
                            {lengthLabel}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                )}
                <ElevationProfile
                  data={elevationData}
                  progressMeters={null}
                  heightPx={70}
                  showDistanceLabels={false}
                  // Wave 14db: Profil-Linie segment-weise in Trail-Farben
                  // einfärben (gleiche Datenquelle wie die Badges oben).
                  segments={
                    previewElevationSegments.length > 0
                      ? previewElevationSegments
                      : undefined
                  }
                />
                {/* Kompakte Stats-Zeile unter dem Profil — Anstieg/Abstieg
                    aus den Samples on-the-fly gerechnet, mit dem gleichen
                    Deadband (4m) wie rideRecorder + shared/geo.ts:
                    elevationStats. So sieht der User die Eckdaten sofort,
                    ohne dass er die Tour erst speichern muss. */}
                <ElevationStatsLine elevations={elevationData.elevations} />
              </div>
            )}

          {/* Wave 14ch: alte inline "Save as custom trail"-Pille entfernt —
              die echten Action-Buttons sind unten rechts in der Bottom-Bar
              (Save / Save & ride now). Hier nur noch eine kurze Success-
              Meldung wenn nach Save (ohne autoRide) die Karte sichtbar bleibt. */}
          {savedCustomId && (
            <div className="mt-3 text-[12px] text-emerald-300/90">
              ✓ Saved. Find it under NAVI → Custom tours.
            </div>
          )}
        </div>
      )}

      {/* Fehler-Banner */}
      {errorMsg && !route && (
        <div className="absolute left-3 right-3 top-40 z-10 rounded-xl bg-red-500/20 backdrop-blur-sm border border-red-400/40 p-3 text-red-100 text-sm">
          {errorMsg}
        </div>
      )}

      {/* Stage 6m: dieselbe BottomBar wie auf MapPage — Skala + Difficulty-
          Filter + POIs an gewohnter Stelle. Höhen-Badge bewusst weggelassen
          (im Plan-Modus gibt's keinen aktiven Trail dessen Höhe Sinn ergäbe;
          die User's GPS-Höhe wäre nur Rauschen). Sitzt OBERHALB der Action-
          Bar (Undo/Clear/Calculate), damit beide nebeneinander stehen können
          ohne sich zu überlagern. */}
      <div
        className="absolute flex justify-end pointer-events-none z-10"
        style={{
          left: '0.75rem',
          right: '0.75rem',
          // Wave 14eg-fix4: auf 3.5rem = exakt die Oberkante des Calculate-
          // Buttons (pb-4 = 1rem + Button py-2.5 ≈ 2.5rem). Die Pille sitzt
          // damit ohne sichtbaren Gap direkt auf der Action-Bar auf (User-
          // Wunsch "klebt am Action-Bar-Oberkante"). Tiefer geht nicht ohne
          // horizontalen Overlap mit dem rechtsbündigen Calculate-Button —
          // beide sind justify-end, müssen also vertikal stapeln.
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 3.5rem)',
        }}
      >
        <div className="pointer-events-auto flex items-center gap-2.5 px-3 py-1.5 rounded-2xl bg-turbo-900/85 border border-turbo-700/50 backdrop-blur-md shadow-lg">
          <ScaleBar />
          <span className="w-px h-4 bg-turbo-700/60" aria-hidden="true" />
          <DifficultyLegend embedded />
          <span className="w-px h-4 bg-turbo-700/60" aria-hidden="true" />
          <POIButton embedded />
        </div>
      </div>

      {/* Bottom-Action-Bar */}
      <div className="absolute inset-x-0 bottom-0 z-10 safe-bottom px-3 pt-3 pb-4 flex items-center gap-2 bg-gradient-to-t from-turbo-900 via-turbo-900/70 to-transparent pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2 w-full">
          <ActionButton
            onClick={handleUndo}
            disabled={
              computing ||
              (mode === 'stroke'
                ? strokes.length === 0
                : // Wave 14en: Undo greift auch auf Start/End-Pin durch.
                  waypoints.length === 0 && !startPin && !endPin)
            }
            label="Undo"
          />
          <ActionButton
            onClick={handleClear}
            disabled={!hasAnyContent || computing}
            label="Clear"
          />

          <div className="flex-1" />

          {route && fromPitstop ? (
            // ULTRA: Navigation zu einem POI (Voraus) — bei einem Ultra-Rennen
            // will Tom die Route nicht speichern, nur losfahren. Ein Button: RIDE.
            <button
              type="button"
              onClick={handleRideWithoutSave}
              className="rounded-full bg-emerald-500 text-white text-base font-bold px-8 py-2.5 shadow-md active:bg-emerald-600 flex items-center gap-2"
            >
              <span aria-hidden="true">🚴</span>
              RIDE
            </button>
          ) : route ? (
            // Wave 14ch: zwei Aktionen statt einem "Done" das nichts speichert.
            // "Save" persistiert + bleibt auf der Seite (Success-Hinweis im
            // Profil-Card oben). "Save & ride now" persistiert, navigiert zur
            // Map, selektiert den frischen Trail und startet den Ride-Modus.
            // Beide nutzen denselben Default-Namen (User kann später umbennen).
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openSavePrompt()}
                disabled={savingCustom}
                className="rounded-full bg-sky-500 text-white text-sm font-semibold px-4 py-2.5 shadow-md active:bg-sky-600 disabled:opacity-60"
              >
                {savingCustom ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => openSavePrompt({ autoRide: true })}
                disabled={savingCustom}
                className="rounded-full bg-emerald-500 text-white text-sm font-semibold px-4 py-2.5 shadow-md active:bg-emerald-600 disabled:opacity-60"
              >
                Save &amp; ride now
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleCalculate}
              disabled={!canCalculate}
              className="rounded-full bg-sky-500 text-white text-sm font-semibold px-5 py-2.5 shadow-md active:bg-sky-600 disabled:bg-turbo-700/60 disabled:text-white/40"
            >
              {computing ? 'Calculating…' : 'Calculate route'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Stage 6m — Initial-Overlay, das den User fragt wie er planen will.
 * Drei Optionen, als grosse Tap-Targets mit Icon + Beschreibung. Der User
 * kann über "Change plan" oben in der Top-Bar jederzeit hierhin zurück.
 *
 * Close-X (oben rechts) entspricht "pin-to-pin" (Default, kein GPS) — für
 * User die einfach loslegen wollen und nicht den Locate-Flow brauchen.
 */
function PlanModePicker({
  onPick,
  onClose,
  routeMode,
  onRouteModeChange,
  maxDifficultyRaw,
  onMaxDifficultyChange,
}: {
  onPick: (m: PlanMode) => void;
  onClose: () => void;
  /** Wave 14ca-fix22: routeMode boolean — false=MTB, true=ROAD. */
  routeMode: boolean;
  onRouteModeChange: (next: boolean) => void;
  /** Wave 14cx: Aktueller Max-Difficulty-Cap als Raw-String ('all' oder eine
   *  Difficulty). Kommt vom Parent-Hook usePreference. */
  maxDifficultyRaw: string;
  onMaxDifficultyChange: (next: string) => void;
}) {
  // Wave 14eh: Sub-Picker fuer Loop-Frage "von hier oder von einem Pin?".
  // Wird sichtbar wenn der User Loop angetippt hat — er ersetzt dann den
  // From-here / Pin-to-pin / Loop-Block, der Rest (Routing, Difficulty)
  // bleibt sichtbar.
  const [loopAsking, setLoopAsking] = useState(false);
  return (
    <div className="absolute inset-0 z-30 bg-turbo-900/80 backdrop-blur-md flex items-center justify-center px-5">
      <div className="w-full max-w-sm rounded-2xl bg-turbo-900/95 border border-sky-500/30 shadow-2xl p-5">
        <div className="flex items-start gap-2 mb-3">
          <div className="flex-1">
            <h2 className="text-white font-semibold text-base leading-tight">
              How do you want to plan?
            </h2>
            <p className="text-white/60 text-xs mt-1">
              Pick a starting scenario. You can change it anytime.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 -mr-1 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10"
            aria-label="Close"
            title="Close (default: Pin to pin)"
          >
            ✕
          </button>
        </div>

        {/* ULTRA: MTB/Road-Toggle + Max-Difficulty-Frage entfernt — Routing
            ist immer Rennrad (road, Asphalt + Schotter), keine Trails. */}

        {!loopAsking && (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => onPick('from-here')}
              className="text-left rounded-xl border border-sky-500/30 bg-turbo-700/30 hover:bg-turbo-700/50 active:bg-turbo-700/70 transition-colors p-3"
            >
              <div className="flex items-center gap-2 text-white font-semibold text-sm">
                <span>📍</span>
                <span>From my position to a destination</span>
              </div>
              <div className="text-white/60 text-[11px] mt-1">
                Start at your current GPS location. You pick the destination and
                the trails in between.
              </div>
            </button>

            <button
              type="button"
              onClick={() => onPick('pin-to-pin')}
              className="text-left rounded-xl border border-sky-500/30 bg-turbo-700/30 hover:bg-turbo-700/50 active:bg-turbo-700/70 transition-colors p-3"
            >
              <div className="flex items-center gap-2 text-white font-semibold text-sm">
                <span>📌</span>
                <span>From one point to another</span>
              </div>
              <div className="text-white/60 text-[11px] mt-1">
                Tap both pins on the map. No GPS required.
              </div>
            </button>

            {/* ULTRA: Loop-Szenario entfernt — nur from-here + pin-to-pin. */}
          </div>
        )}
      </div>
    </div>
  );
}

/** Wave 14dn: Save-Name-Editor-Modal. Vor jedem persist Custom-Tour bekommt
 *  der User die Chance den Default-Namen anzupassen. Auto-fokussiertes
 *  Text-Input, "Cancel" verlässt, "Save" (oder Enter) bestätigt. */
function SaveNameModal({
  defaultName,
  autoRide,
  busy,
  onCancel,
  onConfirm,
}: {
  defaultName: string;
  autoRide: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState(defaultName);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Auto-Fokus + Select-All damit man den Default sofort komplett
    // überschreiben kann mit dem ersten Tipper.
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => window.clearTimeout(t);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const trimmed = name.trim() || defaultName.trim() || 'Custom route';
    onConfirm(trimmed);
  };

  // Wave 14eh-fix3: Theme beim Mount capturen. Im Light-Mode lassen wir den
  // Input der normalen Tailwind-/html.light-Cascade (funktioniert dort, s.
  // User-Screenshot: heller BG + dunkler Text). NUR im Dark-Mode greift der
  // harte Inline-Style, der das iOS-Safari-Default-Weiss (nativer Input-BG +
  // -webkit-text-fill-color) überfährt. One-shot-Check — reagiert nicht live
  // auf Theme-Switch mid-modal, aber das Modal ist kurzlebig (öffnen → Name →
  // schliessen), daher unkritisch.
  const isLightMode =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('light');

  return (
    <div
      className="absolute inset-0 z-30 bg-turbo-900/80 backdrop-blur-md flex items-center justify-center px-5"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-turbo-900/95 border border-sky-500/40 shadow-2xl p-5">
        <h2 className="text-white font-semibold text-base mb-1">
          Name this tour
        </h2>
        <p className="text-white/60 text-xs mb-3 leading-snug">
          Pick a name that helps you find it later. You can rename it anytime
          in NAVI → My Custom Tours.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            maxLength={80}
            placeholder="e.g. Sunset loop via Cacciatore"
            // Wave 14eh-fix3: Inline-Style NUR im Dark-Mode. iOS Safari
            // überfährt dort den CSS-Background (nativ hell) und `color` via
            // `-webkit-text-fill-color` → weiss-auf-weiss. Der Inline-Style
            // (höchste Specificity) erzwingt dunklen BG + weissen Text +
            // WebkitTextFillColor (der Schlüssel). Im Light-Mode lassen wir
            // den Input der normalen Cascade (heller BG + dunkler Text, s.
            // html.light .text-white-Override) — `undefined` setzt keinen
            // Inline-Style, damit der Light-Look unangetastet bleibt.
            className="w-full appearance-none bg-turbo-800 border border-turbo-700/60 rounded-lg px-3 py-2.5 text-white caret-white text-sm focus:outline-none focus:border-sky-500/70"
            style={
              isLightMode
                ? undefined
                : {
                    backgroundColor: '#1e293b',
                    color: '#ffffff',
                    WebkitTextFillColor: '#ffffff',
                    caretColor: '#ffffff',
                  }
            }
            aria-label="Tour name"
          />
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="flex-1 rounded-lg bg-turbo-700/60 text-white text-sm font-medium py-2.5 active:bg-turbo-700/80 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className={`flex-1 rounded-lg text-white text-sm font-semibold py-2.5 disabled:opacity-60 ${
                autoRide
                  ? 'bg-emerald-500 active:bg-emerald-600'
                  : 'bg-sky-500 active:bg-sky-600'
              }`}
            >
              {busy
                ? 'Saving…'
                : autoRide
                  ? 'Save & ride'
                  : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Wave 14cx: ein Button im Max-Difficulty-Grid. Color-Swatch links, Label
 *  rechts. Selected-State = sky-Border + filled background. */
function DifficultyOption({
  value,
  label,
  selected,
  onPick,
  color,
}: {
  value: string;
  label: string;
  selected: boolean;
  onPick: (next: string) => void;
  /** Trail-Farbe für den Swatch, oder null für "All" (= neutraler Swatch). */
  color: string | null;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(value)}
      className={`rounded-lg py-2 px-2.5 text-[12px] font-medium transition-colors flex items-center gap-2 ${
        selected
          ? 'bg-sky-500/30 border border-sky-400/70 text-white shadow-sm'
          : 'bg-turbo-800/50 border border-turbo-700/50 text-white/70 active:bg-turbo-700/50'
      }`}
      aria-pressed={selected}
    >
      <span
        aria-hidden
        className="inline-block w-3 h-3 rounded-full shrink-0 border border-white/30"
        style={{
          background: color || 'transparent',
          backgroundImage: color
            ? undefined
            : 'linear-gradient(135deg, #34d399, #fbbf24, #ef4444)',
        }}
      />
      <span className="truncate">{label}</span>
    </button>
  );
}

function ActionButton({
  onClick,
  disabled,
  label,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-full bg-turbo-900/70 backdrop-blur-sm border border-turbo-700/60 text-white/90 text-sm px-4 py-2 active:bg-turbo-700/60 disabled:opacity-40"
    >
      {label}
    </button>
  );
}

function ModeToggle({
  mode,
  onChange,
}: {
  mode: Mode;
  onChange: (m: Mode) => void;
}) {
  return (
    <div className="inline-flex rounded-full border border-sky-500/30 bg-turbo-900/70 backdrop-blur-sm p-0.5 text-xs">
      <button
        type="button"
        onClick={() => onChange('pins')}
        className={
          'px-3 py-1 rounded-full transition-colors ' +
          (mode === 'pins'
            ? 'bg-sky-500 text-white font-semibold'
            : 'text-sky-200/80')
        }
      >
        Pins
      </button>
      <button
        type="button"
        onClick={() => onChange('stroke')}
        className={
          'px-3 py-1 rounded-full transition-colors ' +
          (mode === 'stroke'
            ? 'bg-sky-500 text-white font-semibold'
            : 'text-sky-200/80')
        }
      >
        Stroke
      </button>
    </div>
  );
}

function makePinElement(
  kind: 'start' | 'end' | 'via',
  label?: string | number,
): HTMLDivElement {
  const el = document.createElement('div');
  // Stage 6m-fix: via-Pin ist ein zentrierter Kreis ohne Dreieck-Tail.
  // Hintergrund: das Multi-Element-Pin-Layout (relative-Container mit zwei
  // absolute Children) lässt MapLibre's Anchor-Translation auf manchen
  // Browser/Pixel-Density-Kombinationen sub-pixel-rounden, was bei Zoom
  // sichtbar als "Pin verschiebt sich" auffällt. User-Bug-Report 14ce-fix:
  // "wenn ich hin und her zoome verschiebt sich die position vom via pin".
  // Mit einem einzelnen Element + anchor:'center' (der Kreis-Mittelpunkt
  // IST geographic-point) gibt's nichts mehr zu rounden.
  // Start/End behalten den klassischen Tropfen, weil der User die nicht
  // als verschoben wahrgenommen hat — der visuelle Tail hilft auch zu
  // erkennen "das ist das Trail-Ende, nicht ein Wegpunkt".
  //
  // Wave 14eh: optionales label (1, 2, 3 …) wird zentriert im Kreis
  // angezeigt — matched die Nummer im Chip-Row, damit der User beim
  // Hin- und Herschieben der Reihenfolge sofort sieht, welcher Punkt
  // auf der Karte welcher in der Liste ist.
  if (kind === 'via') {
    el.style.cssText = `
      width: 22px;
      height: 22px;
      background: #fbbf24;
      border: 2px solid #92400e;
      border-radius: 50%;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
      box-sizing: border-box;
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #422006;
      font-weight: 700;
      font-size: 12px;
      line-height: 1;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;
    if (label != null) el.textContent = String(label);
    el.dataset.viaLabel = label != null ? String(label) : '';
    return el;
  }

  el.style.cssText = `
    position: relative;
    width: 24px;
    height: 32px;
    pointer-events: none;
  `;
  const colorMain = kind === 'start' ? '#34d399' : '#fb7185';
  const colorStroke = kind === 'start' ? '#065f46' : '#881337';
  el.innerHTML = `
    <div style="
      position: absolute;
      left: 2px;
      top: 0;
      width: 20px;
      height: 20px;
      background: ${colorMain};
      border: 2px solid ${colorStroke};
      border-radius: 50%;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    "></div>
    <div style="
      position: absolute;
      left: 9px;
      top: 16px;
      width: 0;
      height: 0;
      border-left: 3px solid transparent;
      border-right: 3px solid transparent;
      border-top: 14px solid ${colorStroke};
    "></div>
  `;
  return el;
}

function pxToLngLat(
  map: maplibregl.Map,
  px: [number, number],
): [number, number] | null {
  try {
    const ll = map.unproject(px);
    return [ll.lng, ll.lat];
  } catch {
    return null;
  }
}

function displayNameForTrailId(id: string): string {
  const dashIdx = id.indexOf('-');
  const slug = dashIdx >= 0 ? id.slice(dashIdx + 1) : id;
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Wave 14ca-fix64: kompakte Stats-Zeile unterhalb des Höhenprofils in
 * der Custom-Tour-Planung. Berechnet ↑/↓ on-the-fly aus den 100 Samples
 * mit demselben 4m-Deadband wie shared/geo.ts:elevationStats und
 * rideRecorder — konsistent mit dem was Trail-/Run-Detail anzeigt.
 */
function ElevationStatsLine({ elevations }: { elevations: number[] }) {
  if (elevations.length < 2) return null;
  const DEAD = 4; // gleicher Deadband wie ELEVATION_DEADBAND_M
  let gain = 0;
  let loss = 0;
  let last = elevations[0]!;
  for (let i = 1; i < elevations.length; i++) {
    const d = elevations[i]! - last;
    if (Math.abs(d) < DEAD) continue;
    if (d > 0) gain += d;
    else loss += -d;
    last = elevations[i]!;
  }
  const min = Math.min(...elevations);
  const max = Math.max(...elevations);
  return (
    <div className="mt-1 flex items-center gap-3 text-[11px] text-white/75 tabular-nums">
      <span>
        ↑ <span className="text-emerald-300 font-semibold">{Math.round(gain)} m</span>
      </span>
      <span>
        ↓ <span className="text-sky-300 font-semibold">{Math.round(loss)} m</span>
      </span>
      <span className="text-white/45">
        {Math.round(min)}–{Math.round(max)} m
      </span>
    </div>
  );
}
